import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlacesAutocomplete } from '@/components/ui/PlacesAutocomplete';
import EngineReportV1 from '@/components/mfcwl-report/EngineReportV1';
import OBDTab from '@/components/LiveProgressModal/OBDTab';
import LiveProgressServiceHistoryTab from '@/components/LiveProgressModal/ServiceHistoryTab';
import ScanHistory from '@/components/LiveProgressModal/ScanHistory';
import { toast } from 'sonner';
import {
  Briefcase, Upload, Mic, FileText, Code, Loader2, Eye, Download,
  UserPlus, CheckCircle2, AlertTriangle, History, Cpu, Volume2,
  RefreshCw, MapPin, Globe, FileDown, Plus, Search, Edit3,
  Bluetooth, Sparkles, Zap, ChevronDown, CircleDot, Gauge, RotateCcw,
  CheckCircle, Wrench, Activity, Thermometer, Fuel, Wind, Timer, Play, FileUp,
  Database, Car, Copy, Send, Clock, XCircle, ArrowRight, ShieldCheck,
  VolumeX, MinusCircle, ArrowUpDown, Filter, X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BASE = `${API_URL}/b2b/mfcwl/admin`;
const ADMIN_ROLES = new Set(['CEO', 'CTO', 'ADMIN', 'SUPER_ADMIN']);

// All MFCWL timestamps are UTC; the backend serialises some as NAIVE UTC (no
// 'Z'/offset). Treat a tz-less string as UTC, then always render in IST
// (Asia/Kolkata) so the view is correct regardless of the viewer's timezone.
const IST_TZ = 'Asia/Kolkata';
const parseTs = (v) => {
  if (!v) return null;
  let s = v;
  if (typeof s === 'string' && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(s)) s = s + 'Z';
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const fmtDate = (v) => { const d = parseTs(v); return d ? d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: IST_TZ }) : '—'; };
const fmtDateOnly = (v) => { const d = parseTs(v); return d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: IST_TZ }) : '—'; };
const fmtTimeOnly = (v) => { const d = parseTs(v); return d ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: IST_TZ }) : ''; };
const fmtShort = (v) => { const d = parseTs(v); return d ? d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: IST_TZ }) : ''; };
const stripSpaces = (s) => (s || '').replace(/\s+/g, '').toUpperCase();

// ── City alias resolution (parity with Inspections mechanic assign) ──
// Build { alias|name(lower) → canonicalName(lower) } from the /cities list.
function buildCityAliasMap(cities) {
  const map = {};
  (cities || []).forEach(c => {
    const canonical = (c.name || '').toLowerCase().trim();
    if (!canonical) return;
    map[canonical] = canonical;
    (c.aliases || []).forEach(a => {
      const al = (a || '').toLowerCase().trim();
      if (al) map[al] = canonical;
    });
  });
  return map;
}
// Resolve any city name to its canonical form (falls back to itself).
function canonicalCity(city, aliasMap) {
  const c = (city || '').toLowerCase().trim();
  return (aliasMap && aliasMap[c]) || c;
}
// Alias-aware: mechanics whose inspection_cities resolve to the inspection's
// canonical city (Bangalore ↔ Bengaluru etc.). Returns all when no city.
function mechanicsForCity(mechanics, city, aliasMap) {
  const target = canonicalCity(city, aliasMap);
  if (!target) return mechanics || [];
  return (mechanics || []).filter(m =>
    (m.inspection_cities || []).some(mc => canonicalCity(mc, aliasMap) === target));
}


// Collapse Surepass/MFCWL verbose vehicle names → "Make Model Year"
// "Ford India Pvt Ltd" → "Ford"; "3.2 Endeavour 4 4 At Titanium" → "Endeavour"
function compactMakeModel(v) {
  if (!v) return '';
  const stopWords = /\b(india|pvt|private|ltd|limited|motors?|company|corp|corporation|automobiles?|suzuki|group)\b/gi;
  const make = String(v.make || '')
    .replace(stopWords, '')
    .replace(/\s+/g, ' ').trim()
    .split(' ')[0] || '';
  // Model: take first alphabetic word (drop "3.2", "4 4 At Titanium", etc.)
  const model = String(v.model || '')
    .split(/\s+/)
    .find(tok => /^[A-Za-z][A-Za-z-]{1,}$/.test(tok)) || '';
  return [make, model, v.year].filter(Boolean).join(' ').trim();
}
const STATUS_OPTIONS = ['NEW', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'RESCHEDULED', 'CANCELLED_MFCWL', 'CANCELLED_WISEDRIVE'];
const STATUS_LABEL = {
  NEW: 'New',
  ASSIGNED: 'Assigned',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  RESCHEDULED: 'Rescheduled',
  CANCELLED_MFCWL: 'Cancelled by MFCWL',
  CANCELLED_WISEDRIVE: 'Cancelled by Wisedrive',
  CANCELLED: 'Cancelled',
};
const ANALYSIS_STATUS_LABEL = {
  AWAITING_VIDEO: 'Awaiting video',
  QUEUED: 'Queued',
  MIRRORING_OBD: 'Syncing OBD',
  ANALYZING: 'Analyzing…',
  PARTIAL: 'Partial — parts arriving',
  COMPLETED: 'Ready',
  FAILED: 'Failed',
};
const ANALYSIS_STATUS_COLOR = {
  AWAITING_VIDEO: 'bg-gray-100 text-gray-500',
  QUEUED:         'bg-sky-100 text-sky-700',
  MIRRORING_OBD:  'bg-indigo-100 text-indigo-700',
  ANALYZING:      'bg-indigo-100 text-indigo-700',
  PARTIAL:        'bg-amber-100 text-amber-700',
  COMPLETED:      'bg-emerald-100 text-emerald-700',
  FAILED:         'bg-rose-100 text-rose-700',
};
const OUTBOUND_CHIP_META = {
  CONFIRMED:    { label: 'Delivered',  cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
  SENT:         { label: 'Sent',       cls: 'bg-blue-100 text-blue-700',       Icon: Send },
  FAILED:       { label: 'Send failed',cls: 'bg-rose-100 text-rose-700',       Icon: XCircle },
  STALE:        { label: 'Stale',      cls: 'bg-orange-100 text-orange-700',   Icon: RotateCcw },
  VOID_RETRACT: { label: 'Voided',     cls: 'bg-rose-100 text-rose-700',       Icon: XCircle },
};

// ── Per-row PIPELINE chips: Service History · OBD · Engine-AI · API-4 ──
// Clickable → deep-links the drawer to the matching tab.
function PipeChip({ label, cls, onClick, testid, title }) {
  return (
    <button type="button" onClick={onClick} title={title || label} data-testid={testid}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium w-fit
                  cursor-pointer transition hover:ring-1 hover:ring-indigo-300 ${cls}`}>
      {label}
    </button>
  );
}

function _shChip(r) {
  const s = r.service_history_status;
  if (s === 'ATTACHED') return { label: 'SH: Attached', cls: 'bg-emerald-100 text-emerald-700', title: 'Service history records attached' };
  return { label: 'SH: Pending', cls: 'bg-amber-100 text-amber-700', title: 'Service history not attached yet' };
}
function _obdChip(r) {
  const src = (r.obd_source || '').toLowerCase();
  if (r.obd_received && src === 'api') return { label: 'OBD: API ✓', cls: 'bg-emerald-100 text-emerald-700', title: 'OBD received via API-2 (structured JSON)' };
  if (r.obd_received && src === 'pdf') return { label: 'OBD: PDF ✓', cls: 'bg-teal-100 text-teal-700',       title: 'OBD ingested from an uploaded PDF' };
  if (r.obd_received)                  return { label: 'OBD: Ready ✓', cls: 'bg-emerald-100 text-emerald-700', title: 'OBD data available' };
  return { label: 'OBD: Pending', cls: 'bg-amber-100 text-amber-700', title: 'No OBD received yet (API or offline PDF)' };
}
function _aiChip(r) {
  const s = r.analysis_status || 'AWAITING_VIDEO';
  const label = s === 'COMPLETED' ? 'AI: Ready'
    : s === 'FAILED' ? 'AI: Failed'
    : s === 'AWAITING_VIDEO' ? 'AI: Awaiting audio'
    : `AI: ${ANALYSIS_STATUS_LABEL[s] || s}`;
  return { label, cls: ANALYSIS_STATUS_COLOR[s] || 'bg-gray-100 text-gray-500',
           title: 'Engine-sound AI analysis status (takes 6–10 min)' };
}
function _api4Chip(r) {
  const meta = OUTBOUND_CHIP_META[r.outbound_status];
  if (!meta) return { label: 'Final API: —', cls: 'bg-gray-100 text-gray-400', title: 'Final report not sent to MFCWL yet' };
  return { label: `Final API: ${meta.label}`, cls: meta.cls, title: `Final report to MFCWL: ${meta.label}` };
}

// ── API-2 reception + completeness (ops visibility) ──────────────────
// API-2 (/submit) is "received" once the engine audio + km land and the
// Sound-AI pipeline starts (analysis_status leaves AWAITING_VIDEO), or once
// the backend stamps `api2_received_at`.
function _api2Received(r) {
  const s = r.analysis_status || 'AWAITING_VIDEO';
  return !!r.api2_received_at || s !== 'AWAITING_VIDEO';
}
function _api2ReceivedAt(r) {
  return r.api2_received_at || r.analysis_updated_at || null;
}
function _api2Chip(r) {
  if (!_api2Received(r)) {
    return { label: 'API-2: awaiting', cls: 'bg-gray-100 text-gray-400',
             title: 'API-2 /submit (engine audio + km) not received yet' };
  }
  const at = _api2ReceivedAt(r);
  const when = at ? fmtShort(at) : '';
  return { label: `API-2 ✓${when ? ' · ' + when : ''}`, cls: 'bg-emerald-100 text-emerald-700',
           title: at ? `API-2 received ${fmtDate(at)}` : 'API-2 received' };
}
// Amber warning when API-2 landed but fuel/transmission weren't provided
// (both feed the engine-sound AI + final report).
function _api2MissingChip(r) {
  if (!_api2Received(r)) return null;
  const miss = [];
  if (!r.vehicle?.fuel_type)    miss.push('fuel');
  if (!r.vehicle?.transmission) miss.push('transmission');
  if (!miss.length) return null;
  return { label: `API-2: ${miss.join(' & ')} missing`, cls: 'bg-amber-100 text-amber-800',
           title: `API-2 received but ${miss.join(' & ')} not provided — needed for the AI + report` };
}

function Api2Flag({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 font-medium ${ok ? 'text-emerald-600' : 'text-amber-600'}`}
          data-testid={`api2-flag-${label.replace(/\s+/g, '-')}`}>
      <span aria-hidden>{ok ? '✓' : '✗'}</span>{label}
    </span>
  );
}

function PipelineCell({ r, onOpen }) {
  const id = r.inspection_id;
  const missing = _api2MissingChip(r);
  return (
    <div className="flex flex-col gap-1 items-start" data-testid={`pipeline-${id}`}>
      <div className="flex flex-wrap items-center gap-1">
        <PipeChip {..._shChip(r)}  onClick={() => onOpen('service')} testid={`pipe-sh-${id}`} />
        <PipeChip {..._obdChip(r)} onClick={() => onOpen('obd')}     testid={`pipe-obd-${id}`} />
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <PipeChip {..._api2Chip(r)} onClick={() => onOpen('engine')} testid={`pipe-api2-${id}`} />
        <PipeChip {..._aiChip(r)}   onClick={() => onOpen('engine')}  testid={`pipe-ai-${id}`} />
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <PipeChip {..._api4Chip(r)} onClick={() => onOpen('reports')} testid={`pipe-api4-${id}`} />
        {missing && <PipeChip {...missing} onClick={() => onOpen('engine')} testid={`pipe-api2-missing-${id}`} />}
      </div>
    </div>
  );
}


const STATUS_COLOR = {
  NEW:                 'bg-blue-100 text-blue-700',
  ASSIGNED:            'bg-amber-100 text-amber-700',
  IN_PROGRESS:         'bg-indigo-100 text-indigo-700',
  COMPLETED:           'bg-emerald-100 text-emerald-700',
  RESCHEDULED:         'bg-purple-100 text-purple-700',
  CANCELLED_MFCWL:     'bg-rose-100 text-rose-700',
  CANCELLED_WISEDRIVE: 'bg-gray-200 text-gray-600',
  CANCELLED:           'bg-gray-100 text-gray-500',
};

// ═══════════════════════════════════════════════════════════════════
// SOW SLA — live red/amber/green countdown so reps can act at a glance.
// Backend (b2b/sla.py) supplies per-timer {applicable, done, blocked,
// deadline, warn_at}; we derive state + tick a live countdown locally.
// ═══════════════════════════════════════════════════════════════════
const SLA_META = {
  assign:  { label: 'Assign',     full: 'Assign a WiseDrive mechanic — SOW 30 min (from API-1)' },
  history: { label: 'Svc Hist',   full: 'Service history ready — SOW 1h (from API-1)' },
  ai:      { label: 'AI',         full: 'Engine-sound AI complete — SOW 1h (from API-2)' },
  obd:     { label: 'OBD',        full: 'OBD data received — SOW 30 min (from API-2)' },
  api4:    { label: 'Send API-4', full: 'Final data pushed to MFCWL (API-4) — SOW 2h (from API-2)' },
};
const SLA_ORDER = ['assign', 'history', 'ai', 'obd', 'api4'];
// Which primary-pill code a breached / at-risk timer maps to (else generic).
const OVERDUE_CODE = { api4: 'api4_overdue', assign: 'assign_overdue' };
const ATRISK_CODE = { api4: 'api4_due', assign: 'assign_due' };

// Live "now" that ticks every `ms` — drives the countdowns without re-fetching.
function useNow(ms = 15000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

// Derive a single timer's live display state from the backend payload.
function deriveTimer(t, nowMs) {
  if (!t || !t.applicable) return { show: false };
  if (t.done) return { show: true, done: true, state: 'DONE' };
  if (t.blocked) return { show: true, blocked: true, state: 'PENDING' };
  const dl = t.deadline ? new Date(t.deadline).getTime() : null;
  const warn = t.warn_at ? new Date(t.warn_at).getTime() : null;
  // A single threshold can be disabled in Settings → its deadline / warn_at
  // arrives null. If BOTH are off the timer is not applicable (handled above).
  if (dl == null && warn == null) return { show: false };
  // Count down to the deadline when present, else to the warn threshold.
  const target = dl != null ? dl : warn;
  const remMs = target - nowMs;
  let state = 'ON_TRACK';
  if (dl != null && nowMs >= dl) state = 'BREACHED';
  else if (warn != null && nowMs >= warn) state = 'AT_RISK';
  return { show: true, state, remMs };
}

function fmtCountdown(remMs, withSeconds = false) {
  const abs = Math.abs(remMs);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = Math.floor((abs % 60000) / 1000);
  let core;
  if (withSeconds) core = h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m ${String(s).padStart(2, '0')}s`;
  else core = h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
  return remMs < 0 ? `${core} over` : core;
}

const SLA_CHIP_CLS = {
  DONE:     'bg-emerald-100 text-emerald-700 border-emerald-200',
  PENDING:  'bg-gray-100 text-gray-500 border-gray-200',
  ON_TRACK: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  AT_RISK:  'bg-amber-100 text-amber-800 border-amber-300',
  BREACHED: 'bg-rose-100 text-rose-700 border-rose-300 animate-pulse',
};

function SlaChip({ tkey, d, withSeconds = false }) {
  if (!d.show) return null;
  const meta = SLA_META[tkey] || { label: tkey, full: tkey };
  let Icon = Clock, txt;
  if (d.done) { Icon = CheckCircle2; txt = 'On time'; }
  else if (d.blocked) { Icon = Clock; txt = 'Awaiting API-2'; }
  else if (d.state === 'BREACHED') { Icon = AlertTriangle; txt = fmtCountdown(d.remMs, withSeconds); }
  else { Icon = Clock; txt = fmtCountdown(d.remMs, withSeconds); }
  const dlStr = d.done || d.blocked ? '' : (() => {
    // best-effort deadline hint in the tooltip
    return '';
  })();
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium whitespace-nowrap ${SLA_CHIP_CLS[d.state] || SLA_CHIP_CLS.PENDING}`}
      title={meta.full + dlStr}
      data-testid={`sla-chip-${tkey}`}>
      <Icon className="h-3 w-3 shrink-0" /> {meta.label}: {txt}
    </span>
  );
}

// One primary "what to do next" status per row. Priority-ordered so the single
// pill always shows the ONE most urgent action. `rank` drives sorting
// (lower = more urgent); `action` = a rep still needs to do something.
const PRIMARY_META = {
  overdue:        { rank: 0, state: 'BREACHED', label: 'Overdue',          icon: AlertTriangle, action: true },
  api4_overdue:   { rank: 0, state: 'BREACHED', label: 'Send API-4',       icon: Send,          action: true },
  assign_overdue: { rank: 0, state: 'BREACHED', label: 'Assign mechanic',  icon: UserPlus,      action: true },
  at_risk:        { rank: 1, state: 'AT_RISK',  label: 'At risk',          icon: Clock,         action: true },
  api4_due:       { rank: 1, state: 'AT_RISK',  label: 'Send API-4',       icon: Send,          action: true },
  assign_due:     { rank: 1, state: 'AT_RISK',  label: 'Assign mechanic',  icon: UserPlus,      action: true },
  unassigned:     { rank: 2, state: 'ACTION',   label: 'Assign mechanic',  icon: UserPlus,      action: true },
  api4_pending:   { rank: 2, state: 'ACTION',   label: 'Send API-4',       icon: Send,          action: true },
  on_track:       { rank: 4, state: 'ON_TRACK', label: 'On track',         icon: Clock,         action: false },
  awaiting:       { rank: 5, state: 'PENDING',  label: 'Awaiting API-2',   icon: Clock,         action: false },
  done:           { rank: 8, state: 'DONE',     label: 'Done',             icon: CheckCircle2,  action: false },
  stale:          { rank: 9, state: 'NONE',     label: 'No SLA',           icon: MinusCircle,   action: false },
  no_sla:         { rank: 9, state: 'NONE',     label: 'No SLA',           icon: MinusCircle,   action: false },
};

const PRIMARY_CLS = {
  BREACHED: 'bg-rose-100 text-rose-700 border-rose-300 animate-pulse',
  AT_RISK:  'bg-amber-100 text-amber-800 border-amber-300',
  ACTION:   'bg-indigo-100 text-indigo-700 border-indigo-300',
  ON_TRACK: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  PENDING:  'bg-slate-100 text-slate-600 border-slate-200',
  DONE:     'bg-emerald-100 text-emerald-700 border-emerald-200',
  NONE:     'bg-gray-50 text-gray-400 border-gray-200',
};

// Derive the single most-important status for a row, LIVE (recomputed each tick
// so on-track → at-risk → breached transitions happen without a refetch).
function derivePrimary(sla, nowMs) {
  if (!sla || sla.no_sla) return { code: 'no_sla', ...PRIMARY_META.no_sla, sub: '', remMs: Infinity };
  if (sla.done) return { code: 'done', ...PRIMARY_META.done, sub: sla.done_late ? 'late' : 'on time', remMs: Infinity };
  if (sla.stale) return { code: 'stale', ...PRIMARY_META.stale, sub: 'stale', remMs: Infinity };

  const t = sla.timers || {};
  // Live, actionable (pending) timers only.
  const pend = SLA_ORDER
    .map(k => ({ k, d: deriveTimer(t[k], nowMs) }))
    .filter(x => x.d.show && !x.d.done && !x.d.blocked);

  const breached = pend.filter(x => x.d.state === 'BREACHED').sort((a, b) => a.d.remMs - b.d.remMs);
  if (breached.length) {
    const w = breached[0];
    const sub = `${fmtCountdown(w.d.remMs).replace(' over', '')} over`;
    const code = OVERDUE_CODE[w.k] || 'overdue';
    return { code, ...PRIMARY_META[code], sub, remMs: w.d.remMs };
  }
  const atRisk = pend.filter(x => x.d.state === 'AT_RISK').sort((a, b) => a.d.remMs - b.d.remMs);
  if (atRisk.length) {
    const w = atRisk[0];
    const sub = `${fmtCountdown(w.d.remMs)} left`;
    const code = ATRISK_CODE[w.k] || 'at_risk';
    return { code, ...PRIMARY_META[code], sub, remMs: w.d.remMs };
  }
  // Nothing breached / at-risk — surface the next pending ACTION for the rep.
  // 1) mechanic not assigned yet (assign clock still running)
  const assign = deriveTimer(t.assign, nowMs);
  if (assign.show && !assign.done && !assign.blocked) {
    return { code: 'unassigned', ...PRIMARY_META.unassigned,
             sub: assign.remMs != null ? `${fmtCountdown(assign.remMs)} left` : '',
             remMs: assign.remMs ?? Infinity };
  }
  // 2) API-2 in but API-4 not sent yet → send it
  const api4 = deriveTimer(t.api4, nowMs);
  if (api4.show && !api4.done && !api4.blocked) {
    return { code: 'api4_pending', ...PRIMARY_META.api4_pending,
             sub: `${fmtCountdown(api4.remMs)} left`, remMs: api4.remMs };
  }
  // 3) waiting on MFCWL to send API-2 (downstream clocks blocked)
  const anyBlocked = SLA_ORDER.some(k => deriveTimer(t[k], nowMs).blocked);
  if (anyBlocked) {
    return { code: 'awaiting', ...PRIMARY_META.awaiting, sub: '', remMs: Infinity };
  }
  return { code: 'on_track', ...PRIMARY_META.on_track, sub: '', remMs: Infinity };
}

function PrimaryPill({ p }) {
  const Icon = p.icon || Clock;
  const cls = PRIMARY_CLS[p.state] || PRIMARY_CLS.NONE;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap ${cls}`}>
      <Icon className="h-3 w-3 shrink-0" /> {p.label}{p.sub ? <span className="font-normal opacity-80"> · {p.sub}</span> : null}
    </span>
  );
}

// The SLA table cell — one live action pill, with the full submit / service-
// history / API-4 breakdown revealed on hover (portalled popover, never clipped).
function SlaCell({ r, nowMs }) {
  const [open, setOpen] = useState(false);
  const sla = r.sla;
  const p = derivePrimary(sla, nowMs);
  const chips = (sla && !sla.no_sla)
    ? SLA_ORDER.map(k => ({ k, d: deriveTimer(sla.timers?.[k], nowMs) })).filter(x => x.d.show)
    : [];
  return (
    <Popover open={open && chips.length > 0} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className="inline-block cursor-default"
          data-testid={`sla-cell-${r.inspection_id}`}>
          <span data-testid={`sla-primary-${r.inspection_id}`}><PrimaryPill p={p} /></span>
        </span>
      </PopoverTrigger>
      {chips.length > 0 && (
        <PopoverContent
          align="start" side="bottom" sideOffset={4}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className="w-auto p-2"
          data-testid={`sla-breakdown-${r.inspection_id}`}>
          <div className="text-[9px] uppercase tracking-wide text-gray-400 font-semibold mb-1">SOW SLA breakdown</div>
          <div className="flex flex-col gap-1 items-start">
            {chips.map(x => <SlaChip key={x.k} tkey={x.k} d={x.d} />)}
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}

// Urgency for sorting + banner counts — derived from the single primary status.
// rank 0=overdue, 1=at-risk, 2=action(assign/send), 4=on-track, 5=awaiting,
// 8=done, 9=none. `action` = still needs a rep to do something.
function slaUrgency(r, nowMs) {
  const p = derivePrimary(r.sla, nowMs);
  return { rank: p.rank, rem: p.remMs, code: p.code, action: !!p.action };
}

// Top banner: states the SOW rules (so reps KNOW adherence is mandatory) +
// a live count of what's breached / at risk right now.
function SlaBanner({ rows, nowMs, muted, onToggleMute }) {
  const { breached, atRisk } = useMemo(() => {
    let breached = 0, atRisk = 0;
    for (const r of rows) {
      const u = slaUrgency(r, nowMs);
      if (u.rank === 0) breached++;
      else if (u.rank === 1) atRisk++;
    }
    return { breached, atRisk };
  }, [rows, nowMs]);

  const tone = breached > 0
    ? 'border-rose-300 bg-rose-50'
    : atRisk > 0 ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50';

  return (
    <div className={`rounded-lg border px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 ${tone}`}
         data-testid="mfcwl-sla-banner">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
        <ShieldCheck className="h-4 w-4 text-indigo-600" /> MFCWL SOW SLA — must be met to avoid penalties
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-600">
        <span className="inline-flex items-center gap-1"><UserPlus className="h-3 w-3" /> Assign ≤ 30m</span>
        <span className="text-gray-300">·</span>
        <span className="inline-flex items-center gap-1"><History className="h-3 w-3" /> Service history ≤ 1h</span>
        <span className="text-gray-400">(from API-1)</span>
        <span className="text-gray-300">·</span>
        <span className="inline-flex items-center gap-1"><Cpu className="h-3 w-3" /> AI ≤ 1h</span>
        <span className="text-gray-300">·</span>
        <span className="inline-flex items-center gap-1"><Activity className="h-3 w-3" /> OBD ≤ 30m</span>
        <span className="text-gray-300">·</span>
        <span className="inline-flex items-center gap-1"><Send className="h-3 w-3" /> Send API-4 ≤ 2h</span>
        <span className="text-gray-400">(from API-2)</span>
      </div>
      <div className="flex items-center gap-2 ml-auto" data-testid="mfcwl-sla-counts">
        {breached > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 border border-rose-300 px-2 py-0.5 text-[11px] font-semibold animate-pulse"
                data-testid="sla-count-breached">
            <AlertTriangle className="h-3 w-3" /> {breached} breached
          </span>
        )}
        {atRisk > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 text-[11px] font-semibold"
                data-testid="sla-count-atrisk">
            <Clock className="h-3 w-3" /> {atRisk} at risk
          </span>
        )}
        {breached === 0 && atRisk === 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[11px] font-semibold"
                data-testid="sla-count-ok">
            <CheckCircle2 className="h-3 w-3" /> All on track
          </span>
        )}
        {onToggleMute && (
          <button onClick={onToggleMute} data-testid="sla-mute-toggle"
                  title={muted ? 'Breach chime muted — click to unmute' : 'Breach chime on — click to mute'}
                  className={`inline-flex items-center justify-center h-6 w-6 rounded-full border transition-colors ${muted ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-indigo-600 border-indigo-200 hover:bg-indigo-50'}`}>
            {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}

// Soft chime + tab-title flash when an inspection tips into BREACHED, so reps
// notice even on another tab. Chimes once per new breach; respects a mute pref.
function useBreachAlarm(breachedIds) {
  const [muted, setMuted] = useState(() => localStorage.getItem('mfcwl_sla_muted') === '1');
  const seenRef = useRef(new Set());
  const ctxRef = useRef(null);
  const origTitleRef = useRef(typeof document !== 'undefined' ? document.title : '');

  useEffect(() => {
    const unlock = () => {
      try {
        if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        if (ctxRef.current && ctxRef.current.state === 'suspended') ctxRef.current.resume();
      } catch (e) { /* no-op */ }
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => { window.removeEventListener('pointerdown', unlock); window.removeEventListener('keydown', unlock); };
  }, []);

  const chime = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    try {
      const t0 = ctx.currentTime;
      [660, 880].forEach((f, i) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = 'sine'; o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
        const t = t0 + i * 0.18;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.14, t + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
        o.start(t); o.stop(t + 0.4);
      });
    } catch (e) { /* no-op */ }
  }, []);

  useEffect(() => {
    let isNew = false;
    for (const id of breachedIds) { if (!seenRef.current.has(id)) { isNew = true; break; } }
    seenRef.current = new Set(breachedIds);
    if (isNew && !muted) chime();
  }, [breachedIds, muted, chime]);

  useEffect(() => {
    const orig = origTitleRef.current || document.title;
    if (!breachedIds.length) { document.title = orig; return undefined; }
    let on = false;
    const t = setInterval(() => {
      on = !on;
      document.title = on ? `🔴 ${breachedIds.length} SLA BREACH` : orig;
    }, 1000);
    return () => { clearInterval(t); document.title = orig; };
  }, [breachedIds]);

  const toggleMute = useCallback(() => {
    setMuted(m => { const n = !m; localStorage.setItem('mfcwl_sla_muted', n ? '1' : '0'); return n; });
  }, []);

  return { muted, toggleMute };
}

// ── Monthly SLA scoreboard moved to CRM › Dashboard › MFCWL tab
//    (src/components/mfcwl/SlaScoreboard.jsx) ──

// ── Queue filter bar (client-side, all combinable) ──
const DEFAULT_FILTERS = { city: 'all', mechanic: 'all', status: 'all', sh: 'all', obd: 'all', ai: 'all', api4: 'all', from: '', to: '' };
const AI_FILTER_OPTS = [
  ['COMPLETED', 'Ready'], ['AWAITING_VIDEO', 'Awaiting audio'], ['QUEUED', 'Queued'],
  ['ANALYZING', 'Analyzing'], ['MIRRORING_OBD', 'Syncing OBD'], ['PARTIAL', 'Partial'], ['FAILED', 'Failed'],
];
const API4_FILTER_OPTS = [
  ['CONFIRMED', 'Delivered'], ['SENT', 'Sent'], ['STALE', 'Stale'], ['VOID_RETRACT', 'Voided'], ['FAILED', 'Failed'], ['NONE', 'Not sent'],
];

function countActiveFilters(f) {
  let n = 0;
  ['city', 'mechanic', 'status', 'sh', 'obd', 'ai', 'api4'].forEach(k => { if (f[k] !== 'all') n++; });
  if (f.from) n++;
  if (f.to) n++;
  return n;
}

function matchFilters(r, f) {
  if (f.city !== 'all' && (r.location?.city || '') !== f.city) return false;
  if (f.mechanic !== 'all') {
    if (f.mechanic === 'unassigned') { if (r.mechanic) return false; }
    else if ((r.mechanic?.id || '') !== f.mechanic) return false;
  }
  if (f.status !== 'all' && r.status !== f.status) return false;
  if (f.sh !== 'all') {
    const sh = r.service_history_status === 'ATTACHED' ? 'ATTACHED' : 'PENDING';
    if (sh !== f.sh) return false;
  }
  if (f.obd !== 'all') {
    const src = String(r.obd_source || '').toLowerCase();
    if (f.obd === 'API' && !(r.obd_received && src === 'api')) return false;
    if (f.obd === 'PDF' && !(r.obd_received && src === 'pdf')) return false;
    if (f.obd === 'RECEIVED' && !r.obd_received) return false;
    if (f.obd === 'PENDING' && r.obd_received) return false;
  }
  if (f.ai !== 'all' && (r.analysis_status || 'AWAITING_VIDEO') !== f.ai) return false;
  if (f.api4 !== 'all') {
    if (f.api4 === 'NONE') { if (r.outbound_status) return false; }
    else if ((r.outbound_status || '') !== f.api4) return false;
  }
  if (f.from || f.to) {
    const t = parseTs(r.scheduled_at || r.created_at)?.getTime() ?? 0;
    if (f.from && t < new Date(f.from + 'T00:00:00').getTime()) return false;
    if (f.to && t > new Date(f.to + 'T23:59:59').getTime()) return false;
  }
  return true;
}

function FilterBar({ filters, setFilters, cities, mechanics }) {
  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const active = countActiveFilters(filters);
  const sel = 'border border-gray-200 rounded-md text-[11px] px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-300';
  return (
    <div className="rounded-lg border bg-white p-2.5 flex flex-wrap items-center gap-2" data-testid="mfcwl-filter-bar">
      <span className="text-[11px] font-semibold text-gray-500 inline-flex items-center gap-1">
        <Filter className="h-3.5 w-3.5 text-indigo-600" /> Filters
      </span>
      <select className={sel} value={filters.city} onChange={e => set('city', e.target.value)} data-testid="filter-city">
        <option value="all">All cities</option>
        {cities.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <select className={sel} value={filters.mechanic} onChange={e => set('mechanic', e.target.value)} data-testid="filter-mechanic">
        <option value="all">All mechanics</option>
        <option value="unassigned">Unassigned</option>
        {mechanics.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
      <select className={sel} value={filters.status} onChange={e => set('status', e.target.value)} data-testid="filter-status">
        <option value="all">All statuses</option>
        {Object.keys(STATUS_LABEL).map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
      </select>
      <select className={sel} value={filters.sh} onChange={e => set('sh', e.target.value)} data-testid="filter-sh">
        <option value="all">SH: all</option>
        <option value="ATTACHED">SH: Attached</option>
        <option value="PENDING">SH: Pending</option>
      </select>
      <select className={sel} value={filters.obd} onChange={e => set('obd', e.target.value)} data-testid="filter-obd">
        <option value="all">OBD: all</option>
        <option value="API">OBD: API</option>
        <option value="PDF">OBD: PDF</option>
        <option value="RECEIVED">OBD: Received (any)</option>
        <option value="PENDING">OBD: Pending</option>
      </select>
      <select className={sel} value={filters.ai} onChange={e => set('ai', e.target.value)} data-testid="filter-ai">
        <option value="all">AI: all</option>
        {AI_FILTER_OPTS.map(([v, l]) => <option key={v} value={v}>AI: {l}</option>)}
      </select>
      <select className={sel} value={filters.api4} onChange={e => set('api4', e.target.value)} data-testid="filter-api4">
        <option value="all">Final API: all</option>
        {API4_FILTER_OPTS.map(([v, l]) => <option key={v} value={v}>Final API: {l}</option>)}
      </select>
      <span className="inline-flex items-center gap-1">
        <label className="text-[11px] text-gray-500">Date</label>
        <input type="date" className={sel} value={filters.from} onChange={e => set('from', e.target.value)} data-testid="filter-from" title="Appointment from" />
        <span className="text-gray-400 text-[11px]">–</span>
        <input type="date" className={sel} value={filters.to} onChange={e => set('to', e.target.value)} data-testid="filter-to" title="Appointment to" />
      </span>
      {active > 0 && (
        <button onClick={() => setFilters(DEFAULT_FILTERS)}
                className="ml-auto text-[11px] text-rose-600 inline-flex items-center gap-1 hover:underline"
                data-testid="filter-clear">
          <X className="h-3 w-3" /> Clear ({active})
        </button>
      )}
    </div>
  );
}



export default function MfcwlDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState('manpower');
  const [newCount, setNewCount] = useState(0);

  const code = (user?.role_code || user?.role || '').toUpperCase();
  const roles = new Set((user?.roles || []).map(r => (r.code || '').toUpperCase()));
  const isAdmin = ADMIN_ROLES.has(code) || [...roles].some(r => ADMIN_ROLES.has(r));

  // The Manpower tab silently polls for newly-received inspections and reports
  // the count up here so the badge sits at the top-right of the page.
  useEffect(() => {
    const onCount = (e) => setNewCount(e.detail?.count || 0);
    document.addEventListener('mfcwl:new-count', onCount);
    return () => document.removeEventListener('mfcwl:new-count', onCount);
  }, []);

  if (!user) return <div className="p-8 text-gray-500">Loading…</div>;
  if (!isAdmin) {
    return (
      <div className="p-12 text-center" data-testid="mfcwl-access-denied">
        <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
        <h2 className="mt-3 text-lg font-semibold text-gray-700">Admin access required</h2>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5" data-testid="mfcwl-dashboard">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-indigo-600" />
            <h1 className="text-2xl font-semibold tracking-tight">MFCWL</h1>
            <Badge variant="outline" className="ml-2">Mahindra First Choice Wheels · B2B</Badge>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Partner inspection queue, engine sound AI analyses, service history, reports & invoices.
          </p>
        </div>
        {newCount > 0 && (
          <button
            type="button"
            onClick={() => document.dispatchEvent(new CustomEvent('mfcwl:refresh'))}
            data-testid="mfcwl-new-inspections-badge"
            title="New inspections received — click (or press Refresh) to load them into the list"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors animate-pulse">
            <Sparkles className="h-3.5 w-3.5" />
            {newCount} new {newCount === 1 ? 'inspection' : 'inspections'}
          </button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <TabsList data-testid="mfcwl-tabs">
            <TabsTrigger value="manpower" data-testid="mfcwl-tab-manpower">
              <Cpu className="h-3.5 w-3.5 mr-1.5" /> Manpower + Tech
            </TabsTrigger>
            {/* AI Analysis + Invoices tabs hidden for now (not required) */}
          </TabsList>
          {tab === 'manpower' && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm"
                      onClick={() => document.dispatchEvent(new CustomEvent('mfcwl:refresh'))}
                      data-testid="manpower-refresh">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
              </Button>
              <Button size="sm"
                      onClick={() => document.dispatchEvent(new CustomEvent('mfcwl:new-inspection'))}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="new-inspection-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> New Inspection
              </Button>
            </div>
          )}
        </div>

        <TabsContent value="manpower"><ManpowerTab /></TabsContent>
        {/* AI Analysis + Invoices tab panels hidden for now (not required)
        <TabsContent value="ai"><AIAnalysisTab /></TabsContent>
        <TabsContent value="invoices"><InvoicesTab /></TabsContent>
        */}
      </Tabs>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB 1 — Manpower + Tech  (inspections requiring our mechanic)
// ═══════════════════════════════════════════════════════════════════
// ── Unmatched CarPM/Garage Pro OBD scans → ops manual-map panel ──
// Scans whose entered ID matched no MFCWL inspection are held "pending";
// ops maps each to an inspection ID here to attach the OBD data.
function UnmatchedObdPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(true);
  const [mapId, setMapId] = useState({});
  const [attaching, setAttaching] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE}/obd/carpm/unmatched`);
      setItems(res.data?.items || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const attach = async (rawId) => {
    const inspection_id = (mapId[rawId] || '').trim();
    if (!inspection_id) { toast.error('Enter an inspection ID (e.g. 295550128 or MFCWL-295550128)'); return; }
    setAttaching(rawId);
    try {
      const res = await axios.post(`${BASE}/obd/carpm/${rawId}/attach`, { inspection_id });
      toast.success(`Mapped to ${res.data.inspection_id} · ${res.data.dtc_count} DTC(s) attached`);
      await load();
      document.dispatchEvent(new CustomEvent('mfcwl:refresh'));
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      toast.error(
        detail === 'inspection-not-found'
          ? `No inspection found with ID "${inspection_id}" — check the ID and try again.`
          : (detail || 'Failed to attach scan'),
      );
    } finally { setAttaching(null); }
  };

  if (!items.length) return null;
  return (
    <div className="border border-amber-300 bg-amber-50 rounded-lg" data-testid="unmatched-obd-panel">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
        data-testid="unmatched-obd-toggle">
        <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-amber-800">
          <AlertTriangle className="h-4 w-4" /> Unmatched OBD scans ({items.length})
        </span>
        <ChevronDown className={`h-4 w-4 text-amber-700 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <p className="text-[11px] text-amber-700">
            These CarPM / Garage Pro scans arrived with an ID that didn't match any inspection.
            Enter the correct inspection ID to attach the OBD data.
          </p>
          {items.map(it => (
            <div
              key={it.raw_id}
              className="bg-white border border-amber-200 rounded-md p-2 flex flex-wrap items-center gap-2"
              data-testid={`unmatched-row-${it.raw_id}`}>
              <div className="text-[11px] text-gray-600 flex-1 min-w-[220px]">
                <span className="font-medium text-gray-800">ID entered: {it.entered_id || '—'}</span>
                {it.vin ? <span> · VIN {it.vin}</span> : null}
                {it.car_company ? <span> · {it.car_company}</span> : null}
                {it.dtc_count > 0 ? (
                  <span> · {it.dtc_count} DTC</span>
                ) : it.report_url ? (
                  <span className="text-amber-700"> · DTCs in PDF (extracted on attach)</span>
                ) : (
                  <span> · 0 DTC</span>
                )}
                {it.report_url ? (
                  <a href={it.report_url} target="_blank" rel="noreferrer"
                     className="text-indigo-600 underline ml-1" data-testid={`unmatched-pdf-${it.raw_id}`}>PDF</a>
                ) : null}
                <div className="text-[10px] text-gray-400">
                  {it.mechanic_name || ''}{it.mechanic_name ? ' · ' : ''}{fmtDate(it.received_at)}
                </div>
              </div>
              <Input
                value={mapId[it.raw_id] || ''}
                onChange={e => setMapId(m => ({ ...m, [it.raw_id]: e.target.value }))}
                placeholder="Inspection ID (e.g. 295550128)"
                className="h-7 text-[11px] w-52"
                data-testid={`unmatched-input-${it.raw_id}`} />
              <Button
                size="sm"
                onClick={() => attach(it.raw_id)}
                disabled={attaching === it.raw_id}
                className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                data-testid={`unmatched-attach-${it.raw_id}`}>
                {attaching === it.raw_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Attach'}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ManpowerTab() {
  const [rows, setRows] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [cityAliasMap, setCityAliasMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [openTab, setOpenTab] = useState('master');
  const [newOpen, setNewOpen] = useState(false);
  const [editInspection, setEditInspection] = useState(null);
  const [needsOnly, setNeedsOnly] = useState(false);
  const [sortMode, setSortMode] = useState('datetime');   // 'datetime' (default) | 'sla'
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const baselineRef = useRef(null);                        // newest received ts at last load

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [insp, mech, cities] = await Promise.all([
        axios.get(`${BASE}/inspections`),
        axios.get(`${BASE}/mechanics`),
        axios.get(`${API_URL}/cities`).catch(() => ({ data: [] })),
      ]);
      const list = insp.data.inspections || [];
      setRows(list);
      setMechanics(mech.data.mechanics || []);
      setCityAliasMap(buildCityAliasMap(cities.data || []));
      // Advance the "new inspections" baseline to the newest received row so the
      // background count poll only flags inspections that arrive AFTER this load,
      // and clear the badge (these rows are now in the table).
      const maxCreated = list.reduce((mx, r) => {
        const t = parseTs(r.created_at)?.getTime() ?? 0;
        return t > mx ? t : mx;
      }, 0);
      baselineRef.current = new Date(maxCreated || Date.now()).toISOString();
      document.dispatchEvent(new CustomEvent('mfcwl:new-count', { detail: { count: 0 } }));
    } catch { toast.error('Failed to load MFCWL inspections'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // NO auto-refresh of the table — the user works in-place (assigning mechanics,
  // editing statuses) and a silent reload would wipe that. New inspections are
  // surfaced via a lightweight background COUNT poll that does NOT touch the
  // rows; the user pulls them in with the manual Refresh button (or a browser
  // refresh / the "N new" badge).
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      const since = baselineRef.current;
      if (!since) return;
      try {
        const res = await axios.get(`${BASE}/inspections/new-count`, { params: { since } });
        if (!alive) return;
        document.dispatchEvent(new CustomEvent('mfcwl:new-count',
          { detail: { count: res.data?.new_count || 0 } }));
      } catch { /* silent — the badge simply won't update */ }
    };
    const t = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Tick a local clock every 15s to drive the live SLA countdowns. Display only
  // — this does NOT refetch data, so nothing in-progress is lost.
  const nowMs = useNow(15000);

  // DEFAULT sort = inspection date/time (scheduled_at, else received/created_at),
  // NEWEST first — matches the DATE/TIME column. The SLA column header toggles
  // to an SLA-priority sort (most urgent first) and back.
  const sortedRows = useMemo(() => {
    const decorated = rows.map((r, i) => ({ r, i, u: slaUrgency(r, nowMs) }));
    if (sortMode === 'sla') {
      decorated.sort((a, b) => {
        if (a.u.rank !== b.u.rank) return a.u.rank - b.u.rank;
        if (a.u.rank <= 1) {                    // breached / at-risk: least time left first
          const rem = a.u.rem - b.u.rem;
          if (rem) return rem;
        }
        return a.i - b.i;                       // else keep created_at-desc
      });
    } else {
      const whenMs = (r) => parseTs(r.scheduled_at || r.created_at)?.getTime() ?? 0;
      decorated.sort((a, b) => (whenMs(b.r) - whenMs(a.r)) || (a.i - b.i));
    }
    return decorated.map(x => x.r);
  }, [rows, nowMs, sortMode]);

  // Distinct cities for the filter dropdown (from the loaded set).
  const cities = useMemo(
    () => [...new Set(rows.map(r => r.location?.city).filter(Boolean))].sort(),
    [rows]);
  // Apply the (combinable) filter bar on top of the sorted rows.
  const filteredRows = useMemo(
    () => sortedRows.filter(r => matchFilters(r, filters)),
    [sortedRows, filters]);

  // Breach chime + tab-title flash so reps notice even on another screen.
  const breachedIds = useMemo(
    () => sortedRows.filter(r => slaUrgency(r, nowMs).rank === 0).map(r => r.inspection_id),
    [sortedRows, nowMs]);
  const { muted, toggleMute } = useBreachAlarm(breachedIds);

  // "Needs action" = anything a rep must still touch (overdue, at-risk,
  // unassigned, or API-4 ready to send). Lets reps triage the whole board fast.
  const needsCount = useMemo(
    () => filteredRows.filter(r => slaUrgency(r, nowMs).action).length,
    [filteredRows, nowMs]);
  const visibleRows = useMemo(
    () => (needsOnly ? filteredRows.filter(r => slaUrgency(r, nowMs).action) : filteredRows),
    [filteredRows, needsOnly, nowMs]);

  // Listen to header-level Refresh / + New Inspection buttons (lifted out
  // of this tab and into the MFCWL page header for vertical compactness).
  useEffect(() => {
    const onRefresh = () => load();
    const onNew = () => setNewOpen(true);
    document.addEventListener('mfcwl:refresh', onRefresh);
    document.addEventListener('mfcwl:new-inspection', onNew);
    return () => {
      document.removeEventListener('mfcwl:refresh', onRefresh);
      document.removeEventListener('mfcwl:new-inspection', onNew);
    };
  }, [load]);

  return (
    <div className="mt-4 space-y-3">
      <UnmatchedObdPanel />
      {!loading && rows.length > 0 && (
        <SlaBanner rows={rows} nowMs={nowMs} muted={muted} onToggleMute={toggleMute} />
      )}
      {!loading && rows.length > 0 && (
        <FilterBar filters={filters} setFilters={setFilters} cities={cities} mechanics={mechanics} />
      )}
      {!loading && rows.length > 0 && (
        <div className="flex items-center gap-2" data-testid="sla-needs-action-filter">
          <span className="text-[11px] text-gray-500">Show</span>
          <button
            onClick={() => setNeedsOnly(false)}
            data-testid="sla-filter-all"
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${!needsOnly ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            All ({filteredRows.length})
          </button>
          <button
            onClick={() => setNeedsOnly(true)}
            data-testid="sla-filter-needs-action"
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${needsOnly ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-rose-600 border-rose-200 hover:bg-rose-50'} ${needsCount === 0 ? 'opacity-60' : ''}`}>
            <AlertTriangle className="h-3 w-3" /> Needs action ({needsCount})
          </button>
        </div>
      )}
      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-x-auto" data-testid="mfcwl-inspections-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">
                  <span className="inline-flex items-center gap-1">
                    Appointment
                    {sortMode === 'datetime' && <ChevronDown className="h-3 w-3 text-indigo-500" />}
                  </span>
                </th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">City</th>
                <th className="text-left px-3 py-2">Vehicle</th>
                <th className="text-left px-3 py-2">Assign to</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2 w-[200px]">Pipeline</th>
                <th className="text-left px-3 py-2 w-[170px]">
                  <div className="flex items-center gap-2">
                    <span>Report</span>
                    <button
                      type="button"
                      onClick={() => setSortMode(m => (m === 'sla' ? 'datetime' : 'sla'))}
                      data-testid="mfcwl-sla-sort-btn"
                      title={sortMode === 'sla'
                        ? 'Sorted by SLA priority (most urgent first) — click to sort by appointment date/time'
                        : 'Click to sort by SLA priority (most urgent first)'}
                      className={`inline-flex items-center gap-1 uppercase tracking-wide transition-colors ${sortMode === 'sla' ? 'text-indigo-600 font-bold' : 'text-gray-400 hover:text-indigo-600'}`}>
                      SLA
                      {sortMode === 'sla'
                        ? <ChevronDown className="h-3 w-3" />
                        : <ArrowUpDown className="h-3 w-3 opacity-60" />}
                    </button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-gray-400">{needsOnly ? 'Nothing needs action right now 🎉' : 'No inspections yet'}</td></tr>
              ) : visibleRows.map((r) => (
                <Row key={r.wisedrive_id} r={r} mechanics={mechanics}
                     cityAliasMap={cityAliasMap} nowMs={nowMs}
                     onOpen={(tab) => { setOpenTab(typeof tab === 'string' ? tab : 'master'); setOpenId(r.inspection_id); }}
                     onEdit={() => setEditInspection(r)}
                     onRefresh={load} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InspectionDrawer inspectionId={openId} initialTab={openTab} onClose={() => setOpenId(null)} onRefresh={load} />
      <NewInspectionModal open={newOpen} onClose={() => setNewOpen(false)} onCreated={(id) => {
        setNewOpen(false);
        load();
        setOpenTab('master');
        setOpenId(id);
      }} />
      <NewInspectionModal
        open={!!editInspection}
        mode="edit"
        initialInspection={editInspection}
        onClose={() => setEditInspection(null)}
        onCreated={() => { setEditInspection(null); load(); }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// + New Inspection — Surepass RC lookup + Google Places + schedule
// ═══════════════════════════════════════════════════════════════════
const TRANSMISSIONS = ['Manual', 'Automatic', 'AMT', 'CVT', 'DCT'];
const FUELS = ['Petrol', 'Diesel', 'CNG', 'Hybrid', 'Electric', 'LPG'];

function initialForm() {
  return {
    customer_name: '', customer_phone: '',
    location_address: '', location_city: '', location_pincode: '',
    location_lat: null, location_lng: null,
    scheduled_at: '',
    vehicle_make: '', vehicle_model: '', vehicle_year: '',
    vehicle_reg_no: '',
    transmission: '', fuel_type: '',
    km_driven: '', has_turbo: false,
    service_tier: 'manpower_tech',
    notes: '',
    assigned_mechanic_id: '',   // optional — assign on save
  };
}

function NewInspectionModal({ open, onClose, onCreated, mode = 'create', initialInspection = null }) {
  const isEdit = mode === 'edit';
  const [rc, setRc] = useState('');
  const [rcLoading, setRcLoading] = useState(false);
  const [rcFetched, setRcFetched] = useState(false);
  const [mechanics, setMechanics] = useState([]);
  const [form, setForm] = useState(initialForm());
  const [submitting, setSubmitting] = useState(false);

  // Hydrate from initialInspection when opening in edit mode
  useEffect(() => {
    if (!open) return;
    if (isEdit && initialInspection) {
      const insp = initialInspection;
      const v = insp.vehicle || {};
      const loc = insp.location || {};
      const cust = insp.customer || {};
      // datetime-local expects "YYYY-MM-DDTHH:mm" in LOCAL time
      let sched = '';
      if (insp.scheduled_at) {
        const d = new Date(insp.scheduled_at);
        const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
        sched = local.toISOString().slice(0, 16);
      }
      setForm({
        customer_name: cust.name || '', customer_phone: cust.phone || '',
        location_address: loc.address || '', location_city: loc.city || '',
        location_pincode: loc.pincode || '',
        location_lat: loc.lat ?? null, location_lng: loc.lng ?? null,
        scheduled_at: sched,
        vehicle_make: v.make || '', vehicle_model: v.model || '',
        vehicle_year: v.year || '', vehicle_reg_no: v.reg_no || '',
        transmission: v.transmission || '', fuel_type: v.fuel_type || '',
        km_driven: v.km_driven ?? '', has_turbo: !!v.has_turbo,
        service_tier: insp.service_tier || 'manpower_tech',
        notes: insp.notes || '',
        assigned_mechanic_id: insp.mechanic?.id || '',
      });
      setRc(v.reg_no || '');
      setRcFetched(true);   // jump straight to full form
    } else if (!isEdit) {
      setForm(initialForm());
      setRc(''); setRcFetched(false);
    }
  }, [open, isEdit, initialInspection]);

  // Load mechanics once the modal opens (filtered client-side by city)
  useEffect(() => {
    if (!open) return;
    axios.get(`${BASE}/mechanics`)
      .then(r => setMechanics(r.data.mechanics || []))
      .catch(() => {});
  }, [open]);

  // Pre-fill scheduled_at with now + 30 min for CREATE only
  useEffect(() => {
    if (open && !isEdit && !form.scheduled_at) {
      const d = new Date(Date.now() + 30 * 60 * 1000);
      d.setSeconds(0, 0);
      d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5);
      const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString().slice(0, 16);
      setForm(f => ({ ...f, scheduled_at: iso }));
    }
  }, [open, isEdit, form.scheduled_at]);

  const reset = () => {
    setRc(''); setRcFetched(false); setRcLoading(false); setSubmitting(false);
    setForm(initialForm());
  };

  const lookupRc = async () => {
    const clean = rc.replace(/\s+/g, '').toUpperCase();
    if (clean.length < 6) { toast.error('Enter a valid RC number'); return; }
    setRcLoading(true);
    try {
      const r = await axios.get(`${BASE}/rc-lookup/${clean}`);
      const d = r.data;
      setForm(f => ({
        ...f,
        vehicle_reg_no: d.rc_number || clean,
        vehicle_make: d.make || '',
        vehicle_model: d.model || '',
        vehicle_year: d.year || '',
        fuel_type: d.fuel_type || '',
        customer_name: f.customer_name || d.owner_name || '',
      }));
      setRcFetched(true);
      toast.success('Vehicle details fetched');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'RC lookup failed — enter manually');
      setRcFetched(true);  // still let them fill manually
      setForm(f => ({ ...f, vehicle_reg_no: clean }));
    } finally { setRcLoading(false); }
  };

  const onPlaceSelect = (place) => {
    setForm(f => ({
      ...f,
      location_address: place.address || '',
      location_city:    place.city || '',     // always overwrite
      location_lat:     place.latitude ?? null,
      location_lng:     place.longitude ?? null,
      // New city → clear current mechanic selection (city filter may change)
      assigned_mechanic_id: place.city && place.city !== f.location_city ? '' : f.assigned_mechanic_id,
    }));
  };

  const submit = async () => {
    // Required field validation
    const missing = [];
    if (!form.customer_name) missing.push('Customer name');
    if (!form.vehicle_make || !form.vehicle_model || !form.vehicle_year) missing.push('Vehicle details');
    if (!form.transmission) missing.push('Transmission');
    if (!form.fuel_type) missing.push('Fuel type');
    if (!form.km_driven || Number(form.km_driven) < 0) missing.push('Kilometres driven');
    if (!form.location_address) missing.push('Address');
    if (!form.scheduled_at) missing.push('Appointment date & time');
    if (missing.length) { toast.error(`Fill: ${missing.join(', ')}`); return; }

    setSubmitting(true);
    try {
      if (isEdit) {
        // PATCH payload — only send changed fields (backend accepts partial)
        const payload = {
          customer_name:    form.customer_name,
          customer_phone:   form.customer_phone || null,
          location_address: form.location_address,
          location_city:    form.location_city || null,
          location_pincode: form.location_pincode || null,
          location_lat:     form.location_lat,
          location_lng:     form.location_lng,
          scheduled_at:     new Date(form.scheduled_at).toISOString(),
          vehicle_make:     form.vehicle_make,
          vehicle_model:    form.vehicle_model,
          vehicle_year:     Number(form.vehicle_year),
          vehicle_reg_no:   stripSpaces(form.vehicle_reg_no),
          transmission:     form.transmission,
          fuel_type:        form.fuel_type,
          km_driven:        Number(form.km_driven),
          has_turbo:        !!form.has_turbo,
          service_tier:     form.service_tier,
          notes:            form.notes || null,
        };
        const id = initialInspection.inspection_id;
        const r = await axios.patch(`${BASE}/inspections/${id}`, payload);
        // Re-assign mechanic if it changed
        const newMech = form.assigned_mechanic_id;
        const oldMech = initialInspection.mechanic?.id || '';
        if (newMech && newMech !== oldMech) {
          try {
            await axios.post(`${BASE}/inspections/${id}/assign`,
                             { mechanic_id: newMech });
          } catch {
            toast.warning('Inspection updated but mechanic assign failed');
          }
        }
        toast.success(r.data.changed?.length
          ? `Updated · ${r.data.changed.length} field${r.data.changed.length !== 1 ? 's' : ''}`
          : 'Inspection updated');
        onCreated?.(id);
        return;
      }

      const inspection_id = `MFCWL-ADM-${Date.now()}`;
      const payload = {
        inspection_id,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone || null,
        location_address: form.location_address,
        location_city:    form.location_city || null,
        location_pincode: form.location_pincode || null,
        location_lat:     form.location_lat,
        location_lng:     form.location_lng,
        scheduled_at:     new Date(form.scheduled_at).toISOString(),
        vehicle_make:     form.vehicle_make,
        vehicle_model:    form.vehicle_model,
        vehicle_year:     Number(form.vehicle_year),
        vehicle_reg_no:   stripSpaces(form.vehicle_reg_no),
        transmission:     form.transmission,
        fuel_type:        form.fuel_type,
        km_driven:        Number(form.km_driven),
        has_turbo:        !!form.has_turbo,
        service_tier:     form.service_tier,
        notes:            form.notes || null,
      };
      const r = await axios.post(`${BASE}/inspections`, payload);
      if (form.assigned_mechanic_id) {
        try {
          await axios.post(`${BASE}/inspections/${inspection_id}/assign`,
                           { mechanic_id: form.assigned_mechanic_id });
        } catch {
          toast.warning('Inspection created but mechanic assignment failed — use the table dropdown');
        }
      }
      toast.success(`Inspection created · ${r.data.wisedrive_id}`);
      reset();
      onCreated?.(inspection_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEdit ? 'Update failed' : 'Create failed'));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && (onClose?.(), reset())}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"
                     data-testid="new-inspection-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit MFCWL Inspection' : 'New MFCWL Inspection'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: RC lookup — hidden in edit mode, RC number is not editable */}
          {!isEdit && (
          <div>
            <Label className="text-xs text-gray-500">Vehicle Registration Number</Label>
            <div className="flex gap-2 mt-1">
              <Input placeholder="e.g. KA03MN4567"
                     value={rc}
                     onChange={e => setRc(e.target.value.toUpperCase())}
                     className="font-mono"
                     onKeyDown={e => e.key === 'Enter' && lookupRc()}
                     data-testid="rc-input" />
              <Button onClick={lookupRc} disabled={rcLoading || !rc.trim()}
                      data-testid="rc-lookup-btn">
                {rcLoading ? <Loader2 className="h-4 w-4 animate-spin" />
                           : <><Search className="h-3.5 w-3.5 mr-1" /> Fetch</>}
              </Button>
            </div>
            <div className="text-[11px] text-gray-400 mt-1">
              Powered by Surepass · auto-fills Make, Model, Year, Fuel type from the RC database
            </div>
          </div>
          )}

          {isEdit && (
            <div className="bg-slate-50 border rounded p-2 text-xs">
              <span className="text-gray-500">Editing:</span>{' '}
              <span className="font-mono font-semibold">{initialInspection?.inspection_id}</span>{' '}
              · <span className="font-mono">{stripSpaces(form.vehicle_reg_no)}</span>
            </div>
          )}

          {rcFetched && (
            <>
              {/* Step 2: Vehicle details (editable, prefilled) */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <Field label="Make">
                  <Input value={form.vehicle_make}
                         onChange={e => setForm(f => ({ ...f, vehicle_make: e.target.value }))}
                         data-testid="field-make" />
                </Field>
                <Field label="Model">
                  <Input value={form.vehicle_model}
                         onChange={e => setForm(f => ({ ...f, vehicle_model: e.target.value }))}
                         data-testid="field-model" />
                </Field>
                <Field label="Year">
                  <Input type="number" value={form.vehicle_year}
                         onChange={e => setForm(f => ({ ...f, vehicle_year: e.target.value }))}
                         data-testid="field-year" />
                </Field>
                <Field label="Fuel type">
                  <Select value={form.fuel_type} onValueChange={v => setForm(f => ({ ...f, fuel_type: v }))}>
                    <SelectTrigger data-testid="field-fuel"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{FUELS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Transmission *">
                  <Select value={form.transmission} onValueChange={v => setForm(f => ({ ...f, transmission: v }))}>
                    <SelectTrigger data-testid="field-transmission"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{TRANSMISSIONS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Kms driven *">
                  <Input type="number" value={form.km_driven}
                         placeholder="e.g. 48000"
                         onChange={e => setForm(f => ({ ...f, km_driven: e.target.value }))}
                         data-testid="field-km" />
                </Field>
                <Field label="Has Turbo?" full>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" checked={!form.has_turbo}
                             onChange={() => setForm(f => ({ ...f, has_turbo: false }))}
                             data-testid="field-turbo-no" /> No
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" checked={!!form.has_turbo}
                             onChange={() => setForm(f => ({ ...f, has_turbo: true }))}
                             data-testid="field-turbo-yes" /> Yes
                    </label>
                  </div>
                </Field>
              </div>

              {/* Step 3: Customer + Location */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <Field label="Customer name *">
                  <Input value={form.customer_name}
                         onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                         data-testid="field-customer-name" />
                </Field>
                <Field label="Customer phone">
                  <Input value={form.customer_phone}
                         onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                         data-testid="field-customer-phone" />
                </Field>
                <Field label="Address *" full>
                  <PlacesAutocomplete
                    value={form.location_address}
                    onChange={v => setForm(f => ({ ...f, location_address: v }))}
                    onSelect={onPlaceSelect}
                    placeholder="Search Google Maps…"
                    country="in"
                  />
                  {form.location_city && (
                    <div className="text-[11px] text-gray-500 mt-1">
                      City: <b>{form.location_city}</b>
                      {form.location_lat && ` · ${form.location_lat.toFixed(4)}, ${form.location_lng.toFixed(4)}`}
                    </div>
                  )}
                </Field>
                <Field label="Pincode">
                  <Input value={form.location_pincode}
                         onChange={e => setForm(f => ({ ...f, location_pincode: e.target.value }))}
                         data-testid="field-pincode" />
                </Field>
                <Field label="City">
                  <Input value={form.location_city}
                         onChange={e => setForm(f => ({ ...f, location_city: e.target.value, assigned_mechanic_id: '' }))}
                         placeholder="Auto-filled from address"
                         data-testid="field-city" />
                </Field>
                <Field label="Assign mechanic" full>
                  <MechanicSelectInModal
                    mechanics={mechanics}
                    city={form.location_city}
                    value={form.assigned_mechanic_id}
                    onChange={v => setForm(f => ({ ...f, assigned_mechanic_id: v }))}
                  />
                </Field>
              </div>

              {/* Step 4: Schedule + tier */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <Field label="Appointment date & time *">
                  <Input type="datetime-local"
                         value={form.scheduled_at}
                         min={todayLocalIso()}
                         onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))}
                         data-testid="field-scheduled" />
                  <div className="text-[10px] text-gray-400 mt-0.5">Editable — reschedule anytime per engineer availability · your local time (IST)</div>
                </Field>
                <Field label="Service tier">
                  <Select value={form.service_tier} onValueChange={v => setForm(f => ({ ...f, service_tier: v }))}>
                    <SelectTrigger data-testid="field-tier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manpower_tech">Manpower + Tech ({activeCurrencySymbol()}600)</SelectItem>
                      <SelectItem value="manpower_tech_history">Manpower + Tech + Service History ({activeCurrencySymbol()}800)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Notes" full>
                  <Input value={form.notes}
                         placeholder="Optional — internal notes"
                         onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </Field>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button variant="outline" onClick={() => { onClose?.(); reset(); }}>Cancel</Button>
                <Button onClick={submit} disabled={submitting}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="submit-inspection-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {isEdit ? 'Update' : 'Save & Assign'}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <Label className="text-xs text-gray-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// Today at 00:00 in the user's local timezone — usable as <input min=...>
function todayLocalIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 16);
}

// Mechanic dropdown filtered by city (picks from the already-loaded list)
function MechanicSelectInModal({ mechanics, city, value, onChange }) {
  const cityNorm = (city || '').toLowerCase().trim();
  const eligible = cityNorm
    ? mechanics.filter(m =>
        (m.inspection_cities || []).some(c => c.toLowerCase() === cityNorm))
    : mechanics;
  const usingFallback = cityNorm && eligible.length === 0;
  const list = usingFallback ? mechanics : eligible;

  return (
    <div className="space-y-1">
      <Select value={value || '__unassigned__'} onValueChange={v => onChange(v === '__unassigned__' ? '' : v)}>
        <SelectTrigger data-testid="field-mechanic">
          <SelectValue placeholder={city ? `Mechanics in ${city}` : 'Pick a location first'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__unassigned__">— Leave unassigned —</SelectItem>
          {list.length === 0
            ? <SelectItem value="__none__" disabled>No mechanics available</SelectItem>
            : list.map(m => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name} {m.phone ? ` · ${m.phone}` : ''}
                  {(m.inspection_cities || []).length > 0 && (
                    <span className="text-gray-400 ml-1">
                      · {m.inspection_cities.slice(0, 2).join(', ')}
                    </span>
                  )}
                </SelectItem>
              ))}
        </SelectContent>
      </Select>
      {city && usingFallback && (
        <div className="text-[10px] text-amber-600">
          No mechanic mapped to <b>{city}</b> in HR — showing all mechanics as fallback
        </div>
      )}
    </div>
  );
}


// ── Dedicated "Assign mechanic" modal — city-filtered list from HR ────
function AssignMechanicModal({ open, onClose, inspection, mechanics, cityAliasMap, onAssigned }) {
  const [selected, setSelected] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const city = inspection?.location?.city || '';
  const cityNorm = city.toLowerCase().trim();
  // Alias-aware match (Bangalore ↔ Bengaluru) — parity with Inspections.
  const cityMechs = mechanicsForCity(mechanics, city, cityAliasMap);
  const list = (showAll || !cityNorm) ? mechanics : cityMechs;
  const currentMech = inspection?.mechanic;

  useEffect(() => {
    if (open) { setSelected(inspection?.mechanic?.id || ''); setShowAll(false); }
  }, [open, inspection]);

  const save = async () => {
    if (!selected) { toast.error('Select a mechanic'); return; }
    const mechanicId = selected === '__unassign__' ? null : selected;
    setSaving(true);
    try {
      await axios.post(`${BASE}/inspections/${inspection.inspection_id}/assign`,
                       { mechanic_id: mechanicId });
      toast.success(mechanicId ? 'Mechanic assigned' : 'Mechanic unassigned');
      onAssigned?.();
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Assign failed'); }
    finally { setSaving(false); }
  };

  const isUnassign = selected === '__unassign__';

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="assign-mechanic-modal">
        <DialogHeader>
          <DialogTitle>{currentMech ? 'Reassign mechanic' : 'Assign mechanic'}{city ? ` · ${city}` : ''}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!cityNorm && (
            <div className="text-xs text-amber-600">No city on this inspection — showing all mechanics.</div>
          )}

          {/* Unassign option — only when a mechanic is currently assigned */}
          {currentMech && (
            <label
              className={`flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer text-sm ${isUnassign ? 'border-rose-400 bg-rose-50' : 'hover:bg-gray-50'}`}
              data-testid="assign-option-unassign">
              <input type="radio" name="assign-mech" checked={isUnassign}
                     onChange={() => setSelected('__unassign__')} />
              <div className="text-rose-600 italic">— Unassign (remove {currentMech.name}) —</div>
            </label>
          )}

          {list.length === 0 ? (
            <div className="text-sm text-gray-500 py-6 text-center" data-testid="assign-no-mechanics">
              No mechanics mapped to <b>{city}</b> in HR.
              {!showAll && (
                <button className="ml-1 text-indigo-600 underline"
                        onClick={() => setShowAll(true)} data-testid="assign-show-all">
                  Show all mechanics
                </button>
              )}
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto space-y-1" data-testid="assign-mechanic-list">
              {list.map(m => (
                <label key={m.id}
                       className={`flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer text-sm ${selected === m.id ? 'border-indigo-500 bg-indigo-50' : 'hover:bg-gray-50'}`}
                       data-testid={`assign-option-${m.id}`}>
                  <input type="radio" name="assign-mech" checked={selected === m.id}
                         onChange={() => setSelected(m.id)} />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{m.name}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {m.phone || m.email}
                      {(m.inspection_cities || []).length ? ` · ${m.inspection_cities.slice(0, 2).join(', ')}` : ''}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
          {cityNorm && !showAll && cityMechs.length > 0 && (
            <button className="text-[11px] text-gray-500 underline"
                    onClick={() => setShowAll(true)} data-testid="assign-show-all-cities">
              Show mechanics from all cities
            </button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !selected}
                  className={isUnassign ? 'bg-rose-600 hover:bg-rose-700' : 'bg-indigo-600 hover:bg-indigo-700'}
                  data-testid="assign-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} {isUnassign ? 'Unassign' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Reschedule modal — pick a new date/time (coupled to RESCHEDULED) ──
function RescheduleModal({ open, onClose, inspection, onDone }) {
  const [dt, setDt] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let iso = '';
    if (inspection?.scheduled_at) {
      const d = new Date(inspection.scheduled_at);
      iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    }
    setDt(iso);
  }, [open, inspection]);

  const save = async () => {
    if (!dt) { toast.error('Pick a new date & time'); return; }
    setSaving(true);
    try {
      await axios.post(`${BASE}/inspections/${inspection.inspection_id}/status`, {
        status: 'RESCHEDULED', scheduled_at: new Date(dt).toISOString(),
      });
      toast.success('Inspection rescheduled');
      onDone?.();
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reschedule failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose?.()}>
      <DialogContent className="max-w-sm" data-testid="reschedule-modal">
        <DialogHeader><DialogTitle>Reschedule appointment</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs text-gray-500">New appointment date &amp; time</Label>
          <Input type="datetime-local" value={dt} min={todayLocalIso()}
                 onChange={e => setDt(e.target.value)} data-testid="reschedule-datetime" />
          <div className="text-[10px] text-gray-400">Times shown in your local timezone</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-700" data-testid="reschedule-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Row({ r, mechanics, cityAliasMap, nowMs, onOpen, onEdit, onRefresh }) {
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const changeStatus = async (newStatus) => {
    if (newStatus === r.status) return;
    setUpdatingStatus(true);
    try {
      await axios.post(`${BASE}/inspections/${r.inspection_id}/status`, { status: newStatus });
      toast.success(`Status → ${STATUS_LABEL[newStatus] || newStatus}`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Status update failed');
    } finally { setUpdatingStatus(false); }
  };

  const handleStatusSelect = (newStatus) => {
    if (newStatus === 'RESCHEDULED') { setRescheduleOpen(true); return; }
    if (newStatus === r.status) return;
    changeStatus(newStatus);
  };

  const regNo = stripSpaces(r.vehicle?.reg_no);
  const makeModel = compactMakeModel(r.vehicle);
  const scheduled = r.scheduled_at;
  const when = scheduled || r.created_at;

  return (
    <tr className="border-t hover:bg-indigo-50/40" data-testid={`mfcwl-row-${r.inspection_id}`}>
      <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap" data-testid={`when-${r.inspection_id}`}>
        <div className="font-medium">{fmtDateOnly(when)}</div>
        <div className="text-gray-500">{fmtTimeOnly(when)} <span className="text-[9px] text-gray-400">IST</span></div>
        {!scheduled && (
          <div className="text-[9px] uppercase tracking-wide text-amber-600">
            Received · no appointment
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="font-medium">{r.customer?.name}</div>
        <div className="text-xs text-gray-500">{r.customer?.phone}</div>
      </td>
      <td className="px-3 py-2">
        {r.location?.city ? (
          <Popover>
            <PopoverTrigger asChild>
              <button className="inline-flex items-center gap-1 text-sm hover:text-indigo-600 underline-offset-2 hover:underline"
                      data-testid={`city-${r.inspection_id}`}>
                <MapPin className="h-3.5 w-3.5 text-gray-400" />
                {r.location.city}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 text-xs" side="right" align="start">
              <div className="font-semibold text-gray-800 mb-2">Full address</div>
              <div className="space-y-1 text-gray-700">
                <div>{r.location?.address}</div>
                <div className="text-gray-500">
                  {r.location?.city}
                  {r.location?.pincode && ` · ${r.location.pincode}`}
                </div>
                {(r.location?.lat != null && r.location?.lng != null) && (
                  <a
                    href={`https://maps.google.com/?q=${r.location.lat},${r.location.lng}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline inline-flex items-center gap-1 mt-1"
                  >
                    Open in Google Maps <MapPin className="h-3 w-3" />
                  </a>
                )}
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs">
        {regNo ? (
          <>
            <div className="font-mono font-semibold text-sm">{regNo}</div>
            <div className="text-gray-500">{makeModel || '—'}</div>
          </>
        ) : (
          <div className="font-medium">{makeModel || '—'}</div>
        )}
      </td>
      <td className="px-3 py-2">
        {r.mechanic ? (
          <div className="flex items-center gap-2">
            <div className="text-xs min-w-0">
              <div className="font-medium truncate max-w-[160px]">{r.mechanic.name}</div>
              <div className="text-gray-500 truncate max-w-[160px]">{r.mechanic.phone || r.mechanic.email}</div>
            </div>
            <Button size="sm" variant="ghost"
                    className="h-6 w-6 p-0 shrink-0"
                    onClick={() => setAssignOpen(true)}
                    title="Change mechanic"
                    data-testid={`assign-${r.inspection_id}`}>
              <Edit3 className="h-3 w-3 text-gray-500 hover:text-indigo-600" />
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline"
                  onClick={() => setAssignOpen(true)}
                  className="h-8 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  data-testid={`assign-${r.inspection_id}`}>
            <UserPlus className="h-3.5 w-3.5 mr-1" /> Assign
          </Button>
        )}
        <AssignMechanicModal
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          inspection={r}
          mechanics={mechanics}
          cityAliasMap={cityAliasMap}
          onAssigned={onRefresh}
        />
      </td>
      <td className="px-3 py-2">
        <Select value={r.status} onValueChange={handleStatusSelect} disabled={updatingStatus}>
          <SelectTrigger
            className={`w-44 h-7 text-[11px] border ${STATUS_COLOR[r.status] || 'bg-gray-100 text-gray-700'}`}
            data-testid={`status-${r.inspection_id}`}
          >
            <SelectValue placeholder={STATUS_LABEL[r.status] || r.status} />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(s => (
              <SelectItem key={s} value={s} data-testid={`status-opt-${s}`}>{STATUS_LABEL[s] || s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <RescheduleModal
          open={rescheduleOpen}
          onClose={() => setRescheduleOpen(false)}
          inspection={r}
          onDone={onRefresh}
        />
      </td>
      <td className="px-3 py-2 w-[200px] align-top">
        <PipelineCell r={r} onOpen={onOpen} />
      </td>
      <td className="px-3 py-2 w-[190px] align-top">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => onOpen('master')}
                    className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800 h-7 text-[11px] gap-1"
                    data-testid={`open-${r.inspection_id}`}>
              <Eye className="h-3.5 w-3.5" /> View & Edit
            </Button>
            <Button size="sm" variant="ghost" onClick={onEdit}
                    className="h-7 w-7 p-0 text-gray-500 hover:text-indigo-600"
                    title="Edit details (vehicle / customer / schedule)"
                    data-testid={`edit-${r.inspection_id}`}>
              <Edit3 className="h-3.5 w-3.5" />
            </Button>
          </div>
          <SlaCell r={r} nowMs={nowMs} />
        </div>
      </td>
    </tr>
  );
}

// ── Inspection detail drawer (Report / Upload / Complete) ────────────
function InspectionDrawer({ inspectionId, initialTab, onClose, onRefresh }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(null);  // 'svc' | 'sound'
  const [activeTab, setActiveTab] = useState('master');
  useEffect(() => {
    if (inspectionId) setActiveTab(initialTab || 'master');
  }, [inspectionId, initialTab]);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [rescanRequesting, setRescanRequesting] = useState(false);
  const [link, setLink] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);

  const load = useCallback(async () => {
    if (!inspectionId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/inspections/${inspectionId}/report.json`);
      setDoc(r.data);
    } catch { toast.error('Fetch failed'); }
    finally { setLoading(false); }
  }, [inspectionId]);

  const loadAudit = useCallback(async () => {
    if (!inspectionId) return;
    setAuditLoading(true);
    try {
      const r = await axios.get(`${BASE}/inspections/${inspectionId}/audit`);
      setAudit(r.data);
    } catch { /* silent — audit is non-critical */ }
    finally { setAuditLoading(false); }
  }, [inspectionId]);

  useEffect(() => { if (inspectionId) { load(); loadAudit(); } }, [inspectionId, load, loadAudit]);

  const issueLink = useCallback(async () => {
    setLinkLoading(true);
    try {
      const r = await axios.post(`${BASE}/inspections/${inspectionId}/public-link`, { ttl_days: 7 });
      setLink(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Link generation failed');
    } finally { setLinkLoading(false); }
  }, [inspectionId]);

  const uploadServiceHistory = async (file) => {
    setUploading('svc');
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${BASE}/inspections/${inspectionId}/service-history`, fd);
      toast.success('Service history uploaded + AI-extracted');
      load(); loadAudit(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); }
  };

  const uploadSoundAi = async (file) => {
    setUploading('sound');
    // Identical flow to the partner API POST /inspections/{id}/engine-video —
    // runs the orchestrator (versioning + streamed callbacks + sync/verify).
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${BASE}/inspections/${inspectionId}/engine-video`, fd);
      toast.success('Engine video submitted — AI analysis queued');
      load(); loadAudit(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); }
  };

  const reanalyzeEngineAi = async () => {
    setReanalyzing(true);
    try {
      await axios.post(`${BASE}/inspections/${inspectionId}/reanalyze`);
      toast.success('Re-analysis queued — new version in progress');
      load(); loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Re-analyse failed'); }
    finally { setReanalyzing(false); }
  };

  const [verifying, setVerifying] = useState(false);
  const verifyAnalysis = async () => {
    setVerifying(true);
    try {
      const r = await axios.post(`${BASE}/inspections/${inspectionId}/verify-analysis`);
      const st = r.data?.status;
      if (st === 'MATCH') toast.success('Verified — our copy matches ai.wisedrive.com exactly');
      else if (st === 'DRIFT') toast.warning(`Drift detected — ${(r.data.diffs || []).length} field(s) differ from source`);
      else toast.error(r.data?.error || 'Verification error');
      load(); loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Verify failed'); }
    finally { setVerifying(false); }
  };

  const [syncing, setSyncing] = useState(false);
  const syncLatest = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${BASE}/inspections/${inspectionId}/sync-analysis`);
      const st = r.data?.status;
      if (st === 'UPDATED') toast.success(`Pulled latest from ai.wisedrive.com — ${(r.data.changed_fields || []).length} field(s) updated`);
      else if (st === 'UNCHANGED') toast.success('Already up to date with ai.wisedrive.com');
      else toast.error(r.data?.error || 'Sync error');
      load(); loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const [restoring, setRestoring] = useState(null);   // version_id being restored
  const restoreVersion = async (versionId) => {
    if (!versionId) return;
    if (!window.confirm(`Restore analysis version ${versionId} as the latest? A new version is created (history is kept) and it becomes what API-4 sends. Re-send API-4 afterwards.`)) return;
    setRestoring(versionId);
    try {
      const r = await axios.post(`${BASE}/inspections/${inspectionId}/engine-ai/restore`, { version_id: versionId });
      toast.success(`Restored — now the latest version (${r.data?.version_id}). Re-send API-4 to push to MFCWL.`);
      load(); loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
    finally { setRestoring(null); }
  };

  const [forceTriggering, setForceTriggering] = useState(false);
  const forceTrigger = async () => {
    if (!window.confirm('Force re-trigger the AI on the CURRENT analysis? This recovers a stuck run (lost webhook / hung pipeline) without creating a new version.')) return;
    setForceTriggering(true);
    try {
      const r = await axios.post(`${BASE}/inspections/${inspectionId}/force-trigger`);
      const st = r.data?.status;
      if (st === 'RECONCILED') toast.success(`Recovered from ai.wisedrive.com — analysis ${r.data?.resolved || 'finalized'}`);
      else if (st === 'RESUBMITTED') toast.success('Re-submitted to AI — analysis re-queued on the same version');
      else toast.error(r.data?.error || 'Force trigger error');
      load(); loadAudit(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Force trigger failed'); }
    finally { setForceTriggering(false); }
  };

  const requestObdRescan = async () => {
    if (!window.confirm('Request the mechanic to rescan OBD? The current scan stays in history.')) return;
    setRescanRequesting(true);
    try {
      await axios.post(`${BASE}/inspections/${inspectionId}/request-rescan`);
      toast.success('Rescan requested — mechanic will be prompted on next app open');
      load(); loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rescan request failed'); }
    finally { setRescanRequesting(false); }
  };

  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const runObdAiAnalyze = async () => {
    setAiAnalyzing(true);
    try {
      const r = await axios.post(`${BASE}/inspections/${inspectionId}/obd-ai-analyze`);
      const total = r.data?.summary?.total ?? 0;
      toast.success(`AI Analysis complete: ${total} verified code${total !== 1 ? 's' : ''}`);
      load(); loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'AI analysis failed'); }
    finally { setAiAnalyzing(false); }
  };

  const insp = doc?.inspection;
  const events = audit?.events || [];

  return (
    <Dialog open={!!inspectionId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-[98vw] w-[98vw] h-[98vh] max-h-[98vh] p-0 rounded-xl overflow-hidden flex flex-col bg-slate-50 gap-0"
        data-testid="mfcwl-drawer">
        {loading || !doc ? (
          <div className="flex-1 grid place-items-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* ── Sticky Header ────────────────────────────────────── */}
            <div className="flex items-start justify-between gap-6 px-6 py-4 bg-white border-b border-slate-200 shrink-0">
              <div className="flex-1 min-w-0 pr-10">
                <DialogTitle className="text-xl font-semibold text-slate-900 tracking-tight truncate">
                  {insp?.customer?.name} <span className="text-slate-400 font-normal">·</span>{' '}
                  <span className="font-mono text-base text-slate-600">{insp?.inspection_id}</span>
                </DialogTitle>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Badge className={STATUS_COLOR[insp?.status] || 'bg-gray-100'}>{insp?.status}</Badge>
                  {insp?.mechanic?.name && <Badge variant="secondary">Mechanic: {insp.mechanic.name}</Badge>}
                </div>
              </div>
            </div>

            {/* ── Body: vertical sidebar + scrollable content ─────── */}
            <div className="flex flex-1 min-h-0 min-w-0">
              <Tabs value={activeTab} onValueChange={setActiveTab}
                    className="flex flex-1 min-h-0 min-w-0" orientation="vertical">
                {/* Left sidebar nav */}
                <div className="w-56 shrink-0 border-r border-slate-200 bg-white overflow-y-auto p-3">
                  <TabsList
                    className="flex flex-col items-stretch h-auto bg-transparent p-0 gap-1 w-full"
                    data-testid="mfcwl-drawer-tabs">
                    <TabsTrigger value="master"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="tab-master-data">
                      <Database className="h-4 w-4" /> Master Data
                    </TabsTrigger>
                    <TabsTrigger value="engine"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="tab-engine-ai">
                      <Volume2 className="h-4 w-4" /> Engine AI
                    </TabsTrigger>
                    <TabsTrigger value="obd"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="tab-obd2">
                      <Cpu className="h-4 w-4" /> OBD2
                    </TabsTrigger>
                    <TabsTrigger value="service"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="tab-service-history">
                      <History className="h-4 w-4" /> Service History
                    </TabsTrigger>
                    <TabsTrigger value="reports"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="tab-outbound-api">
                      <Send className="h-4 w-4" /> Outbound API
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* Right content */}
                <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-slate-50 min-w-0">
                  <div className="max-w-7xl mx-auto w-full space-y-6 text-sm">
                    {/* ─── Master Data — read-only API 1 + API 2 payload ─── */}
                    <TabsContent value="master" className="mt-0 space-y-4 focus-visible:ring-0">
                      <MasterDataTab doc={doc} inspectionId={inspectionId} />
                    </TabsContent>

                    {/* ─── Engine AI ─── */}
                    <TabsContent value="engine" className="mt-0 space-y-4 focus-visible:ring-0">
                      <EngineAiTab
                        inspectionId={inspectionId}
                        doc={doc}
                        uploading={uploading}
                        onUpload={uploadSoundAi}
                        onReanalyze={reanalyzeEngineAi}
                        reanalyzing={reanalyzing}
                        onVerify={verifyAnalysis}
                        verifying={verifying}
                        onSync={syncLatest}
                        syncing={syncing}
                        onForceTrigger={forceTrigger}
                        forceTriggering={forceTriggering}
                        onRestore={restoreVersion}
                        restoring={restoring}
                        events={events}
                        auditLoading={auditLoading}
                        onRefreshAudit={loadAudit}
                        onRefresh={() => { load(); loadAudit(); }}
                      />
                    </TabsContent>

                    {/* ─── OBD2 — canonical Inspections OBD-2 tab, pixel-identical ─── */}
                    <TabsContent value="obd" className="mt-0 space-y-4 focus-visible:ring-0">
                      <MfcwlObdTabCanonical
                        doc={doc}
                        inspectionId={inspectionId}
                        onRequestRescan={requestObdRescan}
                        rescanRequesting={rescanRequesting}
                        onRefresh={() => { load(); loadAudit(); }}
                      />
                      <TabAuditTrail events={events} loading={auditLoading} onRefresh={loadAudit}
                                     scope="obd" testIdPrefix="obd" />
                    </TabsContent>

                    {/* ─── Service History ─── */}
                    <TabsContent value="service" className="mt-0 space-y-4 focus-visible:ring-0">
                      <LiveProgressServiceHistoryTab
                        inspection={{
                          id: inspectionId,
                          kms_driven: insp?.vehicle?.km_driven,
                          vehicle_make: insp?.vehicle?.make,
                          vehicle_model: insp?.vehicle?.model,
                          car_number: insp?.vehicle?.reg_no,
                          vin_number: insp?.vehicle?.vin,
                        }}
                        currentOdometer={insp?.vehicle?.km_driven}
                        apiBase="/api/b2b/mfcwl/admin/inspections"
                        tabValue="service"
                      />
                      <TabAuditTrail events={events} loading={auditLoading} onRefresh={loadAudit}
                                     scope="service" testIdPrefix="service" />
                    </TabsContent>

                    {/* ─── Outbound API (API 4 → MFCWL) ─── */}
                    <TabsContent value="reports" className="mt-0 space-y-4 focus-visible:ring-0">
                      <OutboundApiTab inspectionId={inspectionId} />
                      <ReportsDeliveryCard
                        inspectionId={inspectionId}
                        doc={doc}
                        link={link}
                        linkLoading={linkLoading}
                        onIssueLink={issueLink}
                      />
                      <TabAuditTrail events={events} loading={auditLoading} onRefresh={loadAudit}
                                     scope="all" testIdPrefix="reports" />
                    </TabsContent>
                  </div>
                </div>
              </Tabs>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Sub-tab: Engine AI
// ═══════════════════════════════════════════════════════════════════
const ANALYSIS_PENDING = ['QUEUED', 'MIRRORING_OBD', 'ANALYZING'];

// ── Master Data (read-only) — API 1 (vehicle) + API 2 (km, engine media, OBD raw) ──
function MdField({ label, value, testid }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-sm font-medium text-slate-800 truncate" title={value ? String(value) : ''}
           data-testid={testid}>
        {value !== undefined && value !== null && value !== '' ? value : <span className="text-slate-300">—</span>}
      </div>
    </div>
  );
}

// Streams one engine media file (video or audio) for a specific analysis version.
function EngineMediaPlayer({ inspectionId, analysis, kind, label }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${BASE}/inspections/${inspectionId}/engine-media`, {
        params: analysis?.version_id ? { version_id: analysis.version_id } : {},
        responseType: 'blob',
      });
      setUrl(URL.createObjectURL(r.data));
    } catch {
      setErr('This engine media file is not available on the server.');
    } finally { setLoading(false); }
  };

  return (
    <div className="mb-4 last:mb-0" data-testid={`md-media-block-${kind}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
        {kind === 'video' ? <Play className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
        {label}
        {analysis?.audio_filename && (
          <span className="font-mono text-slate-400 normal-case">· {analysis.audio_filename}</span>
        )}
      </div>
      {url ? (
        kind === 'video' ? (
          <video src={url} controls playsInline preload="metadata"
                 className="w-full max-h-96 rounded-lg bg-black" data-testid="md-video-player" />
        ) : (
          <audio src={url} controls className="w-full" data-testid="md-audio-player" />
        )
      ) : (
        <Button size="sm" variant="outline" onClick={load} disabled={loading}
                data-testid={kind === 'video' ? 'md-load-video-btn' : 'md-load-audio-btn'}>
          {loading
            ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Loading…</>
            : <><Play className="h-3.5 w-3.5 mr-1" /> {kind === 'video' ? 'Load engine video' : 'Load engine audio'}</>}
        </Button>
      )}
      {err && <p className="text-xs text-rose-500 mt-1.5" data-testid="md-media-error">{err}</p>}
    </div>
  );
}

function MasterDataTab({ doc, inspectionId }) {
  const insp = doc?.inspection || {};
  const v = insp.vehicle || {};
  const analyses = insp.analyses || [];
  const latest = insp.latest_version_id
    ? analyses.find(a => a.version_id === insp.latest_version_id)
    : (analyses.length ? analyses[analyses.length - 1] : null);
  const obd = doc?.obd || null;
  const rawCodes = (obd?.raw?.raw_codes && obd.raw.raw_codes.length)
    ? obd.raw.raw_codes
    : [].concat(obd?.dtc?.critical || [], obd?.dtc?.warning || [], obd?.dtc?.info || [],
                obd?.dtc?.active || [], obd?.dtc?.pending || [], obd?.dtc?.permanent || []);

  const mediaFilename = latest?.audio_filename || '';
  const VIDEO_RE = /\.(mp4|mov|m4v|webm|ogv)$/i;
  const AUDIO_RE = /\.(wav|mp3|m4a|ogg|aac|flac)$/i;
  const withFile = analyses.filter(a => a.audio_file_path);
  const videoAnalysis = [...withFile].reverse().find(a => VIDEO_RE.test(a.audio_filename || ''));
  const audioAnalysis = [...withFile].reverse().find(a => AUDIO_RE.test(a.audio_filename || ''));
  // Unknown-extension fallback (only if neither a video nor audio match was found).
  const otherAnalysis = (!videoAnalysis && !audioAnalysis && withFile.length)
    ? withFile[withFile.length - 1] : null;
  const hasMedia = withFile.length > 0;
  const mediaLabel = [videoAnalysis?.audio_filename, audioAnalysis?.audio_filename]
    .filter(Boolean).join(' · ') || mediaFilename;

  return (
    <div className="space-y-4" data-testid="master-data-tab">
      <div className="flex items-center gap-2 text-[11px] text-slate-400">
        <Database className="h-3.5 w-3.5" /> Read-only master record · inspection <code className="text-slate-500">{inspectionId}</code>
      </div>

      {/* Inspection lifecycle timeline (all times IST) */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-indigo-600" />
            <span className="font-semibold text-sm">Inspection Timeline</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3">
            <MdField label="Created (API 1)" value={insp.created_at ? fmtDate(insp.created_at) : null} testid="md-ts-created" />
            <MdField label="Started" value={insp.inspection_started_at ? fmtDate(insp.inspection_started_at) : null} testid="md-ts-started" />
            <MdField label="Submitted (API 2)" value={insp.api2_received_at ? fmtDate(insp.api2_received_at) : null} testid="md-ts-submitted" />
            <MdField label="Completed" value={insp.status === 'COMPLETED' ? fmtDate(insp.analysis_updated_at || insp.updated_at) : null} testid="md-ts-completed" />
          </div>
        </CardContent>
      </Card>

      {/* API 1 — vehicle basics */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Car className="h-4 w-4 text-indigo-600" />
            <span className="font-semibold text-sm">Vehicle Details</span>
            <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">API 1</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
            <MdField label="Make" value={v.make} testid="md-make" />
            <MdField label="Model" value={v.model} testid="md-model" />
            <MdField label="Year" value={v.year} testid="md-year" />
            <MdField label="Fuel Type" value={v.fuel_type} testid="md-fuel" />
            <MdField label="Transmission" value={v.transmission} testid="md-transmission" />
            <MdField label="Registration No." value={v.reg_no} testid="md-regno" />
          </div>
        </CardContent>
      </Card>

      {/* API 1 — customer + location */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus className="h-4 w-4 text-indigo-600" />
            <span className="font-semibold text-sm">Customer Details</span>
            <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">API 1</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
            <MdField label="Name" value={insp.customer?.name} testid="md-customer-name" />
            <MdField label="Phone" value={insp.customer?.phone} testid="md-customer-phone" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-4 w-4 text-indigo-600" />
            <span className="font-semibold text-sm">Location Details</span>
            <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">API 1</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3">
            <div className="md:col-span-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Full Address</div>
              <div className="text-sm font-medium text-slate-800" data-testid="md-location-address">
                {insp.location?.address || <span className="text-slate-300">—</span>}
              </div>
            </div>
            <MdField label="City" value={insp.location?.city} testid="md-location-city" />
            <MdField label="Pincode" value={insp.location?.pincode} testid="md-location-pincode" />
          </div>
        </CardContent>
      </Card>

      {/* API 2 — km + engine media */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Gauge className="h-4 w-4 text-indigo-600" />
            <span className="font-semibold text-sm">Odometer &amp; Engine Media</span>
            <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">API 2</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3 mb-4">
            <MdField label="KM Driven"
                     value={v.km_driven != null ? `${Number(v.km_driven).toLocaleString('en-IN')} km` : null}
                     testid="md-km" />
            <MdField label="Media File" value={mediaLabel || null} testid="md-media-file" />
            <MdField label="Uploaded" value={latest?.created_at ? fmtDate(latest.created_at) : null} testid="md-media-date" />
            <MdField label="AI Source"
                     value={latest?.from_cache != null
                       ? (latest.from_cache
                           ? `Cached${latest.cached_analysis_id ? ` (from ${latest.cached_analysis_id})` : ''}`
                           : 'Fresh run')
                       : null}
                     testid="md-ai-source" />
          </div>

          {/* API-2 completeness — the inputs the engine-sound AI + report need */}
          <div className="border-t pt-3 mb-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]"
               data-testid="md-api2-completeness">
            <span className="uppercase tracking-wider text-slate-400">AI inputs</span>
            <Api2Flag ok={v.km_driven != null} label="km" />
            <Api2Flag ok={hasMedia}            label="engine audio" />
            <Api2Flag ok={!!v.fuel_type}       label="fuel" />
            <Api2Flag ok={!!v.transmission}    label="transmission" />
          </div>

          <div className="border-t pt-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">
              Original Engine Media
            </div>
            {!hasMedia ? (
              <p className="text-xs text-slate-400 italic" data-testid="md-no-media">No engine media uploaded yet.</p>
            ) : (
              <>
                {videoAnalysis && (
                  <EngineMediaPlayer inspectionId={inspectionId} analysis={videoAnalysis}
                                     kind="video" label="Engine video" />
                )}
                {audioAnalysis && (
                  <EngineMediaPlayer inspectionId={inspectionId} analysis={audioAnalysis}
                                     kind="audio" label="Engine audio (used for AI analysis)" />
                )}
                {otherAnalysis && (
                  <EngineMediaPlayer inspectionId={inspectionId} analysis={otherAnalysis}
                                     kind="audio" label="Engine recording" />
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* API 2 — OBD raw data */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Cpu className="h-4 w-4 text-indigo-600" />
            <span className="font-semibold text-sm">OBD Raw Data</span>
            <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">API 2</span>
            {rawCodes.length > 0 && <span className="text-[11px] text-slate-400">({rawCodes.length} codes)</span>}
          </div>
          {!obd ? (
            <p className="text-xs text-slate-400 italic" data-testid="md-no-obd">No OBD data received for this inspection.</p>
          ) : (
            <>
              {rawCodes.length > 0 && (
                <div className="overflow-x-auto rounded border" data-testid="md-obd-table">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="text-left px-2 py-1.5 font-medium">Code</th>
                        <th className="text-left px-2 py-1.5 font-medium">Status</th>
                        <th className="text-left px-2 py-1.5 font-medium">ECU</th>
                        <th className="text-left px-2 py-1.5 font-medium">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rawCodes.map((c, i) => {
                        const code = typeof c === 'string' ? c : (c.code || '');
                        const st = typeof c === 'object' ? (c.status || '') : '';
                        const ecu = typeof c === 'object' ? (c.ecuSource || c.ecu_source || '') : '';
                        const desc = typeof c === 'object' ? (c.description || '') : '';
                        return (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-1.5 font-mono font-medium text-slate-800">{code}</td>
                            <td className="px-2 py-1.5 text-slate-600">{st || '—'}</td>
                            <td className="px-2 py-1.5 text-slate-600">{ecu || '—'}</td>
                            <td className="px-2 py-1.5 text-slate-500">{desc || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <details className="mt-3 text-xs">
                <summary className="cursor-pointer text-slate-500 hover:text-indigo-600" data-testid="md-obd-json-toggle">
                  Raw OBD payload (JSON)
                </summary>
                <pre className="text-[10px] bg-slate-50 p-2 rounded max-h-72 overflow-auto mt-1">
{JSON.stringify(obd, null, 2)}
                </pre>
              </details>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}



function fmtElapsed(ms) {
  if (ms == null || ms < 0 || !isFinite(ms)) return '—';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}m ${String(s % 60).padStart(2, '0')}s`;
}

// ── MFCWL manual override modal (mirrors the AI Agent's override screen) ──
const OVERRIDE_RATING_BANDS = [
  { min: 8.5, label: 'A · EXCELLENT', cls: 'bg-emerald-100 text-emerald-700' },
  { min: 7.0, label: 'B · GOOD', cls: 'bg-lime-100 text-lime-700' },
  { min: 5.0, label: 'C · FAIR', cls: 'bg-amber-100 text-amber-700' },
  { min: 3.0, label: 'D · POOR', cls: 'bg-orange-100 text-orange-700' },
  { min: 0, label: 'E · CRITICAL', cls: 'bg-rose-100 text-rose-700' },
];
const overrideBand = (r) => OVERRIDE_RATING_BANDS.find(b => r >= b.min) || OVERRIDE_RATING_BANDS[OVERRIDE_RATING_BANDS.length - 1];
const OVERRIDE_STATUS_OPTS = [
  { v: 'OK', label: 'Normal' },
  { v: 'ELEVATED', label: 'Elevated' },
  { v: 'CRITICAL', label: 'Critical' },
];

function EngineOverrideModal({ inspectionId, result, open, onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [rating, setRating] = useState(0);
  const [weight, setWeight] = useState(15);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !result) return;
    setRows((result.sound_parameters || []).map(p => {
      const s = (p.status || 'OK').toUpperCase();
      return {
        key: p.key,
        name: p.name || p.key,
        measured_db: Math.round((p.level ?? 0) * 1000) / 10,
        ideal_db: Math.round((p.ideal_level ?? 0) * 1000) / 10,
        status: s === 'NORMAL' ? 'OK' : s,
      };
    }));
    setRating(Number(result.engine_health?.rating ?? 0));
    setWeight(Number(result.valuation_impact?.engine_weightage_pct ?? 15));
    setReason('');
  }, [open, result]);

  const maxR = Number(result?.engine_health?.rating_scale_max || 10);
  const nRating = Number(rating) || 0;
  const nWeight = Number(weight) || 0;
  const impact = Math.round(-(1 - (nRating / maxR)) * nWeight * 10) / 10;
  const band = overrideBand(nRating);
  const setRow = (i, field, val) => setRows(rs => rs.map((r, ix) => ix === i ? { ...r, [field]: val } : r));

  // ── live before→after preview (original result vs current edits) ──
  const baseByKey = {};
  (result?.sound_parameters || []).forEach(p => {
    const s = String(p.status || 'OK').toUpperCase();
    baseByKey[p.key] = {
      measured_db: Math.round((p.level ?? 0) * 1000) / 10,
      ideal_db: Math.round((p.ideal_level ?? 0) * 1000) / 10,
      status: s === 'NORMAL' ? 'OK' : s,
    };
  });
  const baseRating = Number(result?.engine_health?.rating ?? 0);
  const baseWeight = Number(result?.valuation_impact?.engine_weightage_pct ?? 15);
  const baseImpact = Math.round(-(1 - (baseRating / maxR)) * baseWeight * 10) / 10;
  const previewFields = [];
  if (baseRating !== nRating) previewFields.push({ label: 'Engine rating', old: baseRating, new: nRating });
  if (baseWeight !== nWeight) previewFields.push({ label: 'Valuation weightage %', old: baseWeight, new: nWeight });
  if (baseImpact !== impact) previewFields.push({ label: 'Valuation impact %', old: `${baseImpact}%`, new: `${impact}%` });
  const previewParams = [];
  rows.forEach(r => {
    const b = baseByKey[r.key] || {};
    const ch = [];
    if (Number(b.measured_db) !== Number(r.measured_db)) ch.push(['measured dB', b.measured_db, Number(r.measured_db)]);
    if (Number(b.ideal_db) !== Number(r.ideal_db)) ch.push(['ideal dB', b.ideal_db, Number(r.ideal_db)]);
    if (b.status !== r.status) ch.push(['status', b.status, r.status]);
    if (ch.length) previewParams.push({ key: r.key, name: r.name, ch });
  });
  const previewCount = previewFields.length + previewParams.length;
  const hasPreview = previewCount > 0;

  const apply = async () => {
    for (const r of rows) {
      const md = Number(r.measured_db), id = Number(r.ideal_db);
      if (Number.isNaN(md) || md < 0 || md > 100 || Number.isNaN(id) || id < 0 || id > 100) {
        toast.error(`${r.name}: dB values must be between 0 and 100`); return;
      }
    }
    if (nRating < 0 || nRating > 10) { toast.error('Engine rating must be between 0 and 10'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${BASE}/inspections/${inspectionId}/engine-ai/override`, {
        engine_rating: nRating,
        valuation_weightage_pct: nWeight,
        reason: reason || null,
        sound_parameters: rows.map(r => ({
          key: r.key, name: r.name,
          measured_db: Number(r.measured_db), ideal_db: Number(r.ideal_db),
          status: r.status,
        })),
      });
      toast.success(res.data?.ai_synced
        ? 'Override applied & synced with AI Agent — re-send API-4 to push to MFCWL'
        : 'Override applied (local recompute — AI override API not live yet) — re-send API-4 to push');
      onClose();
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Override failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="engine-override-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-4 w-4 text-indigo-600" /> MFCWL Override — Engine Sound
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-gray-500 -mt-2">
          Edit the measured/ideal dB, per-sound status and engine rating. On apply we send this to
          the AI Agent, it recomputes the final report, and we store it as the new version that
          feeds API-4. Re-send of API-4 stays manual.
        </p>

        <div className="grid grid-cols-3 gap-3 items-end bg-slate-50 rounded-lg p-3">
          <div>
            <Label className="text-xs">Engine rating (0–10)</Label>
            <Input type="number" step="0.1" min="0" max="10" value={rating}
                   onChange={e => setRating(e.target.value)} data-testid="override-rating-input" />
          </div>
          <div>
            <Label className="text-xs">Valuation weightage %</Label>
            <Input type="number" step="0.1" min="0" max="100" value={weight}
                   onChange={e => setWeight(e.target.value)} data-testid="override-weight-input" />
          </div>
          <div className="text-xs space-y-1">
            <Badge className={band.cls} data-testid="override-band">{band.label}</Badge>
            <div>Valuation impact: <span className="font-semibold text-rose-600" data-testid="override-impact-preview">{impact}%</span></div>
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-100 text-gray-600">
              <tr>
                <th className="text-left px-2 py-1.5">Sound type</th>
                <th className="px-2 py-1.5 w-28">Measured dB</th>
                <th className="px-2 py-1.5 w-28">Ideal dB</th>
                <th className="px-2 py-1.5 w-32">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.key} className="border-t" data-testid={`override-row-${r.key}`}>
                  <td className="px-2 py-1 font-medium text-gray-700">{r.name}</td>
                  <td className="px-2 py-1">
                    <Input type="number" step="0.1" min="0" max="100" value={r.measured_db}
                           onChange={e => setRow(i, 'measured_db', e.target.value)}
                           className="h-8" data-testid={`override-measured-${r.key}`} />
                  </td>
                  <td className="px-2 py-1">
                    <Input type="number" step="0.1" min="0" max="100" value={r.ideal_db}
                           onChange={e => setRow(i, 'ideal_db', e.target.value)}
                           className="h-8" data-testid={`override-ideal-${r.key}`} />
                  </td>
                  <td className="px-2 py-1">
                    <Select value={r.status} onValueChange={v => setRow(i, 'status', v)}>
                      <SelectTrigger className="h-8" data-testid={`override-status-${r.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {OVERRIDE_STATUS_OPTS.map(o => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <Label className="text-xs">Reason (optional)</Label>
          <Input value={reason} onChange={e => setReason(e.target.value)}
                 placeholder="e.g. corrected mic gain on metallic sound" data-testid="override-reason-input" />
        </div>

        {/* ── Live before→after preview ── */}
        <div className="border rounded-lg p-3 bg-indigo-50/40" data-testid="override-preview">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-indigo-800 mb-1.5">
            <ArrowUpDown className="h-3.5 w-3.5" /> Preview changes
            <span className="text-[10px] font-normal text-slate-500">
              {hasPreview ? `${previewCount} change${previewCount !== 1 ? 's' : ''} — review before applying` : 'no changes yet'}
            </span>
          </div>
          {!hasPreview ? (
            <div className="text-[11px] text-slate-400 italic" data-testid="override-preview-empty">
              Edit any value above to see a live before→after preview here.
            </div>
          ) : (
            <div className="space-y-1 text-[11px]">
              {previewFields.map((f, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`override-preview-field-${i}`}>
                  <span className="text-slate-500 w-40 shrink-0">{f.label}</span>
                  <span className="text-rose-600 line-through">{String(f.old)}</span>
                  <ArrowRight className="h-3 w-3 text-gray-400 shrink-0" />
                  <span className="text-emerald-700 font-semibold">{String(f.new)}</span>
                </div>
              ))}
              {previewParams.length > 0 && (
                <div className="border-t pt-1 mt-1 space-y-0.5">
                  <div className="text-[10px] text-slate-400">
                    {previewParams.length} sound parameter{previewParams.length !== 1 ? 's' : ''} changed
                  </div>
                  {previewParams.map((p) => (
                    <div key={p.key} className="flex flex-wrap items-center gap-x-2 gap-y-0.5"
                         data-testid={`override-preview-param-${p.key}`}>
                      <span className="text-slate-700 font-medium w-32 shrink-0">{p.name}</span>
                      {p.ch.map((c, j) => (
                        <span key={j} className="whitespace-nowrap">
                          <span className="text-slate-400">{c[0]}: </span>
                          <span className="text-rose-600 line-through">{String(c[1])}</span>
                          <ArrowRight className="inline h-2.5 w-2.5 mx-0.5 text-gray-400" />
                          <span className="text-emerald-700 font-semibold">{String(c[2])}</span>
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="override-cancel-btn">Cancel</Button>
          <Button onClick={apply} disabled={saving || !rows.length}
                  className="bg-indigo-600 hover:bg-indigo-700" data-testid="override-apply-btn">
            {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Applying…</> : 'Apply changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EngineAiTab({ inspectionId, doc, uploading, onUpload, onReanalyze, reanalyzing, onVerify, verifying, onSync, syncing, onForceTrigger, forceTriggering, onRestore, restoring, events, auditLoading, onRefreshAudit, onRefresh }) {
  const insp = doc?.inspection || {};
  const analyses = insp.analyses || [];
  const latest = insp.latest_version_id
    ? analyses.find(a => a.version_id === insp.latest_version_id)
    : (analyses.length ? analyses[analyses.length - 1] : null);
  const analysisStatus = insp.analysis_status || 'AWAITING_VIDEO';
  const hasAudioOnDisk = analyses.some(a => a.audio_file_path);
  const isPending = ANALYSIS_PENDING.includes(analysisStatus)
    || ANALYSIS_PENDING.includes(latest?.status);
  const isPartial = analysisStatus === 'PARTIAL' || latest?.status === 'PARTIAL';
  const sectionStatus = latest?.section_status;
  const hasPartial = sectionStatus && typeof sectionStatus === 'object'
    && Object.values(sectionStatus).some(Boolean);
  const isFailed = latest?.status === 'FAILED' || analysisStatus === 'FAILED';
  const isCompleted = latest?.status === 'COMPLETED' && !!latest?.result;

  // Live elapsed-time ticker + auto-poll while the analysis is in progress.
  const [tick, setTick] = useState(0);
  const [overrideOpen, setOverrideOpen] = useState(false);
  useEffect(() => {
    if (!isPending && !isPartial) return undefined;
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [isPending, isPartial]);
  useEffect(() => {
    if ((!isPending && !isPartial) || !onRefresh) return undefined;
    const p = setInterval(() => onRefresh(), 30000);   // auto-poll every 30s
    return () => clearInterval(p);
  }, [isPending, isPartial, onRefresh]);

  const startedAt = latest?.submitted_at || latest?.created_at;
  const elapsedMs = startedAt ? (Date.now() - new Date(startedAt).getTime()) : null;

  return (
    <div className="space-y-3" data-testid="engine-ai-tab">
      <EngineOverrideModal inspectionId={inspectionId} result={latest?.result}
        open={overrideOpen} onClose={() => setOverrideOpen(false)} onDone={onRefresh} />
      <Card><CardContent className="p-3">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="font-semibold flex items-center gap-1.5">
            <Volume2 className="h-4 w-4 text-indigo-600" /> Engine Sound AI
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`text-[10px] ${ANALYSIS_STATUS_COLOR[analysisStatus] || 'bg-gray-100 text-gray-500'}`}
                   data-testid="engine-ai-status-badge">
              {ANALYSIS_STATUS_LABEL[analysisStatus] || analysisStatus}
            </Badge>
            <label className="cursor-pointer">
              <input type="file" accept="audio/*,.wav,.mp3,.m4a,.mp4" className="hidden"
                     onChange={e => e.target.files?.[0] && onUpload(e.target.files[0])}
                     data-testid="upload-sound-input" />
              <Button size="sm" variant="outline" disabled={uploading === 'sound'} asChild>
                <span>{uploading === 'sound'
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Uploading…</>
                  : <><Upload className="h-3.5 w-3.5 mr-1" /> Upload audio</>}</span>
              </Button>
            </label>
            <Button size="sm" variant="default" className="bg-violet-600 hover:bg-violet-700"
                    disabled={reanalyzing || !hasAudioOnDisk}
                    onClick={onReanalyze}
                    title={hasAudioOnDisk ? 'Fallback: re-run AI as a FRESH version on the saved audio' : 'No audio on disk to re-analyse'}
                    data-testid="reanalyze-engine-ai-btn">
              {reanalyzing
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Re-analysing…</>
                : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Re-analyse</>}
            </Button>
            {onForceTrigger && analyses.length > 0 && (
              <Button size="sm" variant="outline"
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      disabled={forceTriggering}
                      onClick={onForceTrigger}
                      title="Fallback recovery: un-stick the CURRENT run (pull result from ai.wisedrive.com if the webhook was lost, else re-submit the same version). Use when automation fails."
                      data-testid="force-trigger-engine-ai-btn">
                {forceTriggering
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Recovering…</>
                  : <><Zap className="h-3.5 w-3.5 mr-1" /> Force re-trigger</>}
              </Button>
            )}
            {latest?.sound_ai_analysis_id && onSync && (
              <Button size="sm" variant="outline" disabled={syncing} onClick={onSync}
                      title="Pull the current report from ai.wisedrive.com (applies any source-side edits / cached data)"
                      data-testid="sync-latest-btn">
                {syncing
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Syncing…</>
                  : <><RefreshCw className="h-3.5 w-3.5 mr-1" /> Sync latest</>}
              </Button>
            )}
            {isCompleted && (
              <Button size="sm" variant="outline"
                      className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      onClick={() => setOverrideOpen(true)}
                      title="Manually override the sound numbers / rating — sent to the AI Agent to recompute the final report, then feeds API-4"
                      data-testid="engine-override-btn">
                <Edit3 className="h-3.5 w-3.5 mr-1" /> Override
              </Button>
            )}
            {isCompleted && onVerify && (
              <>
                <Button size="sm" variant="outline" disabled={verifying} onClick={onVerify}
                        title="Re-fetch from ai.wisedrive.com and compare field-by-field to our stored copy"
                        data-testid="verify-analysis-btn">
                  {verifying
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Verifying…</>
                    : <><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Verify vs source</>}
                </Button>
                {latest?.verify_status && (
                  <Badge className={`text-[10px] ${latest.verify_status === 'MATCH'
                        ? 'bg-emerald-100 text-emerald-700'
                        : latest.verify_status === 'DRIFT'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-rose-100 text-rose-700'}`}
                         title={latest.last_verified_at ? `Last verified ${fmtDate(latest.last_verified_at)}` : ''}
                         data-testid="verify-status-badge">
                    {latest.verify_status === 'MATCH' ? 'Verified ✓'
                      : latest.verify_status === 'DRIFT' ? `Drift ⚠ (${(latest.verify_diffs || []).length})`
                      : 'Verify error'}
                  </Badge>
                )}
              </>
            )}
          </div>
        </div>

        {analyses.length === 0 ? (
          <p className="text-xs text-gray-400 italic">
            No engine sound submitted yet. Mechanic uploads via the field app, or admin via "Upload audio".
          </p>
        ) : (
          latest && (
            <div className="text-xs border rounded p-2 bg-violet-50/50">
              <div className="flex items-center justify-between mb-1">
                <div className="font-medium text-violet-900">
                  Latest version · <code className="text-[10px]">{latest.version_id}</code>
                </div>
                <Badge variant="outline" className="text-[10px]">{latest.status}</Badge>
              </div>
              <div className="text-[10px] text-gray-600">
                File: {latest.audio_filename || '—'} · uploaded {fmtDate(latest.created_at)}
              </div>
              {(latest.partner_revision != null || latest.source_updated_at || latest.last_synced_at) && (
                <div className="text-[10px] text-violet-700 mt-0.5 flex flex-wrap gap-x-2"
                     data-testid="engine-ai-source-meta">
                  {latest.partner_revision != null && <span>source rev {latest.partner_revision}</span>}
                  {latest.source_updated_at && <span>· edited on AI {fmtDate(latest.source_updated_at)}</span>}
                  {latest.last_synced_at && <span>· synced {fmtDate(latest.last_synced_at)}</span>}
                </div>
              )}
            </div>
          )
        )}
      </CardContent></Card>

      {/* ── In-progress state — long AI run (typically 6-10 min) ── */}
      {isPending && !hasPartial && (
        <Card data-testid="engine-ai-in-progress">
          <CardContent className="p-6 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto" />
            <div className="mt-3 font-semibold text-slate-800">AI analysis in progress</div>
            <div className="text-xs text-slate-500 mt-1">
              Current stage: <b>{ANALYSIS_STATUS_LABEL[analysisStatus] || analysisStatus}</b>
              {elapsedMs != null && <> · elapsed <b data-testid="engine-ai-elapsed">{fmtElapsed(elapsedMs)}</b></>}
            </div>
            <p className="text-[11px] text-slate-400 mt-2 max-w-md mx-auto leading-relaxed">
              Engine sound analysis typically takes <b>6–10 minutes</b>. This panel refreshes
              automatically every 30 seconds — you can also refresh manually. It's safe to close
              this drawer; the report will be here when it's ready.
            </p>
            {onRefresh && (
              <Button size="sm" variant="outline" className="mt-3" onClick={onRefresh}
                      data-testid="engine-ai-inprogress-refresh">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh now
              </Button>
            )}
            {onForceTrigger && (
              <div className="mt-3">
                <Button size="sm" variant="outline"
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        disabled={forceTriggering} onClick={onForceTrigger}
                        title="Stuck too long? Pull the result from ai.wisedrive.com (lost webhook) or re-submit the same version."
                        data-testid="engine-ai-inprogress-force-trigger">
                  {forceTriggering
                    ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Recovering…</>
                    : <><Zap className="h-3.5 w-3.5 mr-1" /> Force re-trigger</>}
                </Button>
                <div className="text-[10px] text-slate-400 mt-1">Fallback if automation stalls</div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Failed state ── */}
      {!isPending && isFailed && (
        <Card data-testid="engine-ai-failed">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-8 w-8 text-rose-500 mx-auto" />
            <div className="mt-3 font-semibold text-slate-800">Analysis failed</div>
            <div className="text-xs text-rose-600 mt-1 max-w-md mx-auto break-words">
              {latest?.error || 'The AI could not complete this analysis.'}
            </div>
            <Button size="sm" variant="default" className="mt-3 bg-violet-600 hover:bg-violet-700"
                    disabled={reanalyzing || !hasAudioOnDisk} onClick={onReanalyze}
                    data-testid="engine-ai-failed-reanalyze">
              {reanalyzing
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Re-analysing…</>
                : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Re-analyse</>}
            </Button>
            {onForceTrigger && (
              <Button size="sm" variant="outline"
                      className="mt-3 ml-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                      disabled={forceTriggering} onClick={onForceTrigger}
                      title="Pull the result from ai.wisedrive.com (lost webhook) or re-submit the same version without a new upload."
                      data-testid="engine-ai-failed-force-trigger">
                {forceTriggering
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Recovering…</>
                  : <><Zap className="h-3.5 w-3.5 mr-1" /> Force re-trigger</>}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Completed / partial — schema-v1 report (renders parts as they arrive) ── */}
      {(isCompleted || hasPartial) && (
        <EngineReportV1
          result={latest.result}
          sectionStatus={hasPartial ? sectionStatus : undefined}
        />
      )}

      {/* ── Integrity drift panel — our copy differs from the AI source ── */}
      {!isPending && isCompleted && latest?.verify_status === 'DRIFT'
        && Array.isArray(latest?.verify_diffs) && latest.verify_diffs.length > 0 && (
        <Card data-testid="verify-drift-panel">
          <CardContent className="p-3">
            <div className="flex items-center gap-1.5 text-amber-700 font-semibold text-sm mb-1">
              <AlertTriangle className="h-4 w-4" /> Drift vs ai.wisedrive.com source ({latest.verify_diffs.length})
            </div>
            <div className="text-[11px] text-slate-500 mb-2">
              Our stored copy differs from the live AI record{latest.last_verified_at ? ` · last checked ${fmtDate(latest.last_verified_at)}` : ''}.
              Re-analyse to pull the latest, or ask the AI team to reconcile.
            </div>
            <div className="border rounded divide-y">
              {latest.verify_diffs.map((d, i) => (
                <div key={i} className="text-[11px] flex items-start gap-2 px-2 py-1.5"
                     data-testid={`verify-diff-${i}`}>
                  <code className="text-slate-700 shrink-0 w-56 truncate" title={d.field}>{d.field}</code>
                  <span className="text-rose-600 break-all">ours: {JSON.stringify(d.ours)}</span>
                  <span className="text-emerald-700 break-all">source: {JSON.stringify(d.source)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Completed but no structured result (edge case) ── */}
      {!isPending && !isFailed && latest?.status === 'COMPLETED' && !latest?.result && (
        <Card><CardContent className="p-4 text-xs text-gray-400 italic" data-testid="engine-ai-noresult">
          Analysis completed but no structured report was returned.
        </CardContent></Card>
      )}

      {/* ── Override & change log — audit of manual edits / re-analyses ── */}
      <EngineChangeLog events={events} loading={auditLoading} onRefresh={onRefreshAudit}
                       latestVersionId={latest?.version_id} onRestore={onRestore} restoring={restoring} />
    </div>
  );
}

// ── Engine-AI change log: manual overrides (with before→after diff),
//    re-analyses, verify & sync events — read from the audit trail ──
const ENGINE_LOG_ACTIONS = ['engine_ai.override', 'engine_ai.reanalyze', 'engine_ai.verify', 'engine_ai.sync', 'engine_ai.restore'];
const OVERRIDE_FIELD_LABELS = {
  engine_rating: 'Engine rating',
  engine_health_status: 'Health status',
  valuation_weightage_pct: 'Valuation weightage %',
  valuation_impact_pct: 'Valuation impact %',
};
const fmtLogVal = (v) => (v === null || v === undefined || v === '') ? '—' : String(v);

function EngineChangeLog({ events, loading, onRefresh, latestVersionId, onRestore, restoring }) {
  const logs = (events || []).filter(e => ENGINE_LOG_ACTIONS.some(a => (e.action || '').includes(a)));
  return (
    <Card data-testid="engine-change-log">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 font-semibold text-sm text-slate-800">
            <History className="h-4 w-4 text-indigo-600" /> Override &amp; Change Log
            <span className="text-[10px] text-gray-400 font-normal">
              {logs.length} event{logs.length !== 1 ? 's' : ''}
            </span>
          </div>
          {onRefresh && (
            <Button size="sm" variant="ghost" className="h-6 text-[10px]"
                    onClick={onRefresh} disabled={loading} data-testid="engine-change-log-refresh">
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
        {loading && !logs.length ? (
          <div className="text-[11px] text-gray-400 italic">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="text-[11px] text-gray-400 italic" data-testid="engine-change-log-empty">
            No overrides or re-analyses recorded yet. Manual edits via "Override" appear here with a full before→after trail.
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {logs.map((e, i) => (
              <EngineLogEntry key={e.id || i} e={e}
                              latestVersionId={latestVersionId}
                              onRestore={onRestore} restoring={restoring} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EngineLogEntry({ e, latestVersionId, onRestore, restoring }) {
  const d = e.details || {};
  const action = e.action || '';
  const isOverride = action.includes('engine_ai.override');
  const actor = e.actor_email || d.edited_by || e.actor_id || 'admin';
  const when = e.ts ? fmtDate(e.ts) : '';
  const meta = AUDIT_LABELS[action] || { label: action };
  const diff = d.diff || {};
  const fields = diff.fields || [];
  const params = diff.params || [];
  const hasDiff = fields.length > 0 || params.length > 0;
  // Version this event produced (override / reanalyse / restore all set new_version_id)
  const producedVersion = d.new_version_id;
  const isCurrent = producedVersion && latestVersionId && producedVersion === latestVersionId;
  const canRestore = !!producedVersion && !isCurrent && !!onRestore;
  return (
    <div className="border rounded p-2 bg-slate-50 text-[11px]" data-testid="engine-log-entry">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-medium text-slate-800">{meta.label}</div>
        <div className="flex items-center gap-1.5">
          {isOverride && (
            <Badge className={`text-[9px] ${d.ai_synced ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
                   data-testid="engine-log-sync-badge">
              {d.ai_synced ? 'Synced to AI Agent ✓' : 'Local only (AI override API offline)'}
            </Badge>
          )}
          {isCurrent && (
            <Badge className="text-[9px] bg-indigo-100 text-indigo-700" data-testid="engine-log-current-badge">
              Current version
            </Badge>
          )}
          {canRestore && (
            <Button size="sm" variant="outline"
                    className="h-6 text-[10px] border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    disabled={restoring === producedVersion}
                    onClick={() => onRestore(producedVersion)}
                    title={`Restore version ${producedVersion} as the latest (feeds API-4)`}
                    data-testid={`engine-log-restore-${producedVersion}`}>
              {restoring === producedVersion
                ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Restoring…</>
                : <><RotateCcw className="h-3 w-3 mr-1" /> Restore this version</>}
            </Button>
          )}
        </div>
      </div>
      <div className="text-[10px] text-gray-500 mb-1">
        {actor}{when ? ` · ${when}` : ''}{producedVersion ? ` · v ${producedVersion}` : ''}
      </div>

      {isOverride && hasDiff && (
        <div className="space-y-1 mt-1">
          {fields.map((f, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`engine-log-field-${f.field}`}>
              <span className="text-slate-500 w-40 shrink-0">{OVERRIDE_FIELD_LABELS[f.field] || f.field}</span>
              <span className="text-rose-600 line-through">{fmtLogVal(f.old)}</span>
              <ArrowRight className="h-3 w-3 text-gray-400 shrink-0" />
              <span className="text-emerald-700 font-semibold">{fmtLogVal(f.new)}</span>
            </div>
          ))}
          {params.length > 0 && (
            <div className="mt-1 border-t pt-1">
              <div className="text-[10px] text-slate-400 mb-0.5">
                {params.length} sound parameter{params.length !== 1 ? 's' : ''} changed
              </div>
              <div className="space-y-0.5">
                {params.map((p, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-x-2 gap-y-0.5"
                       data-testid={`engine-log-param-${p.key}`}>
                    <span className="text-slate-700 font-medium w-32 shrink-0">{p.name}</span>
                    {Object.entries(p.changes || {}).map(([k, val]) => (
                      <span key={k} className="text-[10px] whitespace-nowrap">
                        <span className="text-slate-400">{k.replace(/_/g, ' ')}: </span>
                        <span className="text-rose-600 line-through">{fmtLogVal(val.old)}</span>
                        <ArrowRight className="inline h-2.5 w-2.5 mx-0.5 text-gray-400" />
                        <span className="text-emerald-700 font-semibold">{fmtLogVal(val.new)}</span>
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
          {d.reason && <div className="text-[10px] text-slate-500 italic mt-1">Reason: {d.reason}</div>}
        </div>
      )}

      {isOverride && !hasDiff && (
        <div className="text-[10px] text-slate-400 italic">
          {d.engine_rating != null ? `Rating set to ${d.engine_rating}` : 'Override applied'} · no field-level diff recorded (older override).
        </div>
      )}

      {!isOverride && (
        <div className="text-[10px] text-slate-600">
          {action.includes('verify') && `Verify result: ${d.verify_status || '—'}${d.diff_count != null ? ` · ${d.diff_count} diff${d.diff_count !== 1 ? 's' : ''}` : ''}`}
          {action.includes('sync') && `Sync result: ${d.sync_status || '—'}${d.changed != null ? ` · ${d.changed} field${d.changed !== 1 ? 's' : ''} changed` : ''}`}
          {action.includes('reanalyze') && `New version ${d.new_version_id || '—'}${d.source_version_id ? ` (from ${d.source_version_id})` : ''}`}
          {action.includes('restore') && `Restored from ${d.restored_from || '—'} → new version ${d.new_version_id || '—'}`}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Sub-tab: OBD2 — mirrors the B2C "DTC Code Analysis" UX
// Scan status + rescan | DTC count banner | AI analysis with
// ECU-grouped, expandable code rows (causes / symptoms / actions).
// ═══════════════════════════════════════════════════════════════════
function MfcwlObdTabCanonical({ doc, inspectionId, onRequestRescan, rescanRequesting, onRefresh }) {
  const obd = doc?.obd || null;
  const insp = doc?.inspection || {};
  const v = insp.vehicle || {};

  // Flatten DTC codes from the scan (raw.raw_codes preferred, else buckets).
  const dtcCodes = useMemo(() => {
    if (!obd) return [];
    const raw = obd.raw?.raw_codes;
    if (Array.isArray(raw) && raw.length) return raw;
    const d = obd.dtc || {};
    return [].concat(
      d.critical || [], d.warning || [], d.info || [],
      d.active || [], d.pending || [], d.permanent || [], d.history || [],
    );
  }, [obd]);

  const inspectionProp = useMemo(() => ({
    id: inspectionId,
    obd_ai_analysis: insp.obd_ai_analysis || null,
    obd_scanning_enabled: insp.obd_scanning_enabled !== false,
    car_number: v.reg_no || '',
    rc_number: v.reg_no || '',
    manufacturer: v.make || '',
    model: v.model || '',
    year: v.year || '',
    obd_scan: { report_selection: insp.obd_report_selection || null },
  }), [inspectionId, insp, v]);

  const liveProgressData = useMemo(() => ({
    obd_scan: {
      completed: !!obd,
      scanned_at: obd?.completed_at || obd?.created_at || null,
      source: obd?.source,
      report_selection: insp.obd_report_selection || null,
      data: obd ? {
        dtc_codes: dtcCodes,
        raw_codes: dtcCodes,
        total_errors: dtcCodes.length,
        live_data: obd.raw?.live_data || [],
        source: obd.source,
      } : null,
    },
  }), [obd, dtcCodes, insp.obd_report_selection]);

  return (
    <div data-testid="mfcwl-obd-tab">
      <OBDTab
        canEdit
        inspection={inspectionProp}
        liveProgressData={liveProgressData}
        dtcCodes={dtcCodes}
        requestingRescan={rescanRequesting}
        requestObdRescan={onRequestRescan}
        onRefresh={onRefresh}
        apiBase="/b2b/mfcwl/admin/inspections"
      />
    </div>
  );
}

function ObdTab({ doc, onRequestRescan, rescanRequesting, onAiAnalyze, aiAnalyzing, inspectionId, onRefresh }) {
  const obd = doc?.obd;
  const insp = doc?.inspection || {};
  const rescanPending = !!insp.obd_rescan_requested;
  const aiAnalysis = insp.obd_ai_analysis || null;

  // PDF fallback upload (admin-only; canEdit is effectively admin here)
  const fileInputRef = useRef(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const handleObdPdfUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !inspectionId) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('PDF must be under 10 MB');
      return;
    }
    setUploadingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('pdf', file);
      const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/inspections/${inspectionId}`;
      // Step 1: upload — returns job_id
      const res = await axios.post(
        `${baseUrl}/obd-pdf-upload`,
        fd,
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          timeout: 30000,
        }
      );
      const jobId = res.data?.job_id;
      if (!jobId) throw new Error('Server did not return a job id');
      // Step 2: poll for completion (5 min ceiling, 3s interval)
      const start = Date.now();
      let done = false;
      let lastJob = null;
      while (Date.now() - start < 5 * 60 * 1000) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const j = await axios.get(`${baseUrl}/obd-pdf-job/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
          });
          lastJob = j.data;
          if (lastJob?.status === 'completed' || lastJob?.status === 'failed') {
            done = true;
            break;
          }
        } catch (_) { /* transient — keep polling */ }
      }
      if (!done) {
        toast.error('Extraction timed out after 5 minutes. Please try a smaller PDF.');
      } else if (lastJob.status === 'failed') {
        toast.error(lastJob.error || 'PDF extraction failed');
      } else {
        const n = lastJob.result?.dtc_codes_count || 0;
        toast.success(
          n > 0
            ? `Extracted ${n} DTC code${n !== 1 ? 's' : ''} from ${file.name}`
            : `No DTCs found in ${file.name} — modules summary saved`
        );
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'PDF upload failed');
    } finally {
      setUploadingPdf(false);
    }
  };

  // Live sensor readings — passthrough from the mechanic's scan when present.
  const liveData = obd?.raw?.live_data || obd?.live_data || [];

  // Raw count — sum of buckets so we know if "Analyze Codes" should be enabled
  const rawCount = useMemo(() => {
    if (!obd) return 0;
    const d = obd.dtc || {};
    return (d.critical?.length || 0) + (d.warning?.length || 0) + (d.info?.length || 0);
  }, [obd]);

  // Verified (AI-enriched) codes with normalized status
  const verified = useMemo(() => {
    if (!aiAnalysis) return [];
    return (aiAnalysis.codes_for_report || []).map(c => ({
      ...c,
      ai_severity: c.ai_severity === 'IMPORTANT' ? 'CRITICAL' : (c.ai_severity === 'CRITICAL' ? 'CRITICAL' : 'MINOR'),
      status_label: c.status === 'Pending' || c.status === 'pending' ? 'Pending'
        : c.status === 'Active' || c.status === 'active' || c.status === 'Confirmed' ? 'Active'
        : 'History',
    }));
  }, [aiAnalysis]);

  // Group by ECU source (fallback: derive from code prefix)
  const grouped = useMemo(() => {
    const g = {};
    verified.forEach(c => {
      const sys = c.ecuSource || c.ecu_source
        || (c.code?.startsWith('P') ? 'Engine Control Module (ECM)'
           : c.code?.startsWith('C') ? 'Chassis/ABS'
           : c.code?.startsWith('B') ? 'Body Control'
           : 'Network/Other');
      if (!g[sys]) g[sys] = { name: sys, codes: [], critical: 0, minor: 0 };
      g[sys].codes.push(c);
      if (c.ai_severity === 'CRITICAL') g[sys].critical++; else g[sys].minor++;
    });
    return Object.values(g).sort((a, b) => b.critical - a.critical);
  }, [verified]);

  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedCode, setExpandedCode] = useState(null);

  // Open the first group by default so the user immediately sees the codes
  useEffect(() => {
    if (grouped.length && expandedGroups.size === 0) {
      setExpandedGroups(new Set([grouped[0].name]));
    }
  }, [grouped.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-3" data-testid="obd-tab">
      {/* ── PDF fallback upload (admin) ───────────────────────────────── */}
      <div
        className={`rounded-lg p-3 border-2 transition-colors ${
          !obd ? 'border-purple-300 bg-purple-50' : 'border-slate-200 bg-slate-50'
        }`}
        data-testid="obd-pdf-upload-card"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${!obd ? 'bg-purple-600' : 'bg-slate-400'}`}>
              <FileUp className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 text-sm">Upload OBD Report PDF</p>
              <p className="text-[11px] text-gray-600">
                {!obd
                  ? 'No scan yet? Upload a PDF — AI will extract DTC codes automatically.'
                  : 'Fallback: re-extract from a fresh PDF (replaces the latest scan).'}
              </p>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleObdPdfUpload}
            style={{ display: 'none' }}
            data-testid="obd-pdf-upload-input"
          />
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingPdf}
            className={`${!obd ? 'bg-purple-600 hover:bg-purple-700' : 'bg-slate-600 hover:bg-slate-700'} text-white`}
            data-testid="obd-pdf-upload-btn"
          >
            {uploadingPdf ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analyzing PDF…</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" />Upload PDF</>
            )}
          </Button>
        </div>
      </div>

      {/* ── Row: Scan status card + DTC count banner ─────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <div className={`rounded-lg p-3 border ${obd ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${obd ? 'bg-green-500' : 'bg-gray-300'}`}>
              <Gauge className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm text-gray-900">{obd ? 'Scan Done' : 'Pending'}</p>
              {obd?.completed_at && (
                <p className="text-[10px] text-gray-500 truncate">{fmtDate(obd.completed_at)}</p>
              )}
            </div>
          </div>
          <Button
            variant={obd ? 'outline' : 'default'}
            size="sm"
            onClick={onRequestRescan}
            disabled={rescanRequesting || !obd}
            className={`w-full mt-2 text-xs ${obd ? '' : 'bg-blue-600 hover:bg-blue-700'}`}
            data-testid="request-rescan-btn">
            {rescanRequesting
              ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Requesting…</>
              : <><RotateCcw className="h-3 w-3 mr-1" /> Rescan</>}
          </Button>
          {rescanPending && (
            <div className="mt-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 text-center">
              Rescan pending
            </div>
          )}
        </div>
        <div className={`lg:col-span-3 p-3 rounded-lg border flex items-center gap-2 ${rawCount > 0 ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
          {rawCount > 0 ? (
            <>
              <AlertTriangle className="h-5 w-5 text-blue-600" />
              <span className="text-sm text-blue-800 font-medium">{rawCount} DTC code{rawCount !== 1 ? 's' : ''} found</span>
            </>
          ) : obd ? (
            <>
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-sm text-green-700">No DTC codes detected</span>
            </>
          ) : (
            <>
              <Gauge className="h-5 w-5 text-gray-400" />
              <span className="text-sm text-gray-500 italic">No OBD scan linked yet</span>
            </>
          )}
        </div>
      </div>

      {/* ── DTC Code Analysis ─────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold flex items-center gap-1.5 text-sm">
              <Zap className="h-4 w-4 text-indigo-600" /> DTC Code Analysis
            </div>
            <Badge className="text-[10px]" variant={aiAnalysis ? 'default' : 'outline'}>
              {aiAnalysis ? `${verified.length} verified` : 'Not analyzed'}
            </Badge>
          </div>

          {/* Status bar: analyzed-timestamp + Re-analyze button */}
          <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border mb-3">
            <div className="text-xs text-gray-600">
              {aiAnalysis ? (
                <span>Analyzed: {fmtDate(aiAnalysis.analyzed_at)} <span className="text-gray-400">·</span> Manual</span>
              ) : rawCount > 0 ? (
                <span>Run analysis to classify {rawCount} raw code{rawCount !== 1 ? 's' : ''}</span>
              ) : (
                <span className="italic text-gray-400">No codes to analyze</span>
              )}
            </div>
            <Button
              variant="outline" size="sm"
              onClick={onAiAnalyze}
              disabled={aiAnalyzing || rawCount === 0}
              className="border-purple-300 text-purple-700 hover:bg-purple-50 h-7 text-[11px]"
              data-testid="obd-ai-analyze-btn">
              {aiAnalyzing
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Analyzing…</>
                : <><Zap className="h-3.5 w-3.5 mr-1.5" /> {aiAnalysis ? 'Re-analyze' : 'Analyze Codes'}</>}
            </Button>
          </div>

          {/* Grouped, expandable codes */}
          {aiAnalysis && (verified.length > 0 ? (
            <div className="space-y-2" data-testid="mfcwl-verified-codes">
              {grouped.map((group, gi) => {
                const isOpen = expandedGroups.has(group.name);
                return (
                  <div key={gi} className="border rounded-lg overflow-hidden">
                    <div className="bg-slate-100 px-3 py-2.5 flex items-center justify-between cursor-pointer hover:bg-slate-200"
                         onClick={() => setExpandedGroups(prev => { const s = new Set(prev); s.has(group.name) ? s.delete(group.name) : s.add(group.name); return s; })}>
                      <div className="flex items-center gap-2.5">
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                        <div className="h-8 w-8 rounded-lg bg-slate-700 flex items-center justify-center">
                          <span className="text-white font-bold text-[10px]">
                            {(group.name.match(/\b\w/g) || []).slice(0, 3).join('').toUpperCase() || group.name.slice(0, 3).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-gray-900">{group.name}</p>
                          <p className="text-[11px] text-gray-500">{group.codes.length} code{group.codes.length !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="flex gap-1.5">
                        {group.critical > 0 && <span className="px-2 py-0.5 text-[10px] rounded-full bg-red-100 text-red-700 border border-red-200 font-medium">{group.critical} Critical</span>}
                        {group.minor > 0 && <span className="px-2 py-0.5 text-[10px] rounded-full bg-sky-100 text-sky-700 border border-sky-200 font-medium">{group.minor} Minor</span>}
                      </div>
                    </div>
                    {isOpen && (
                      <div className="divide-y">
                        {group.codes.map((c, ci) => {
                          const isExp = expandedCode === c.code;
                          const hasCauses = c.possible_causes?.length > 0;
                          const hasSymp = c.symptoms?.length > 0;
                          const hasAct = c.recommended_actions?.length > 0;
                          const hasDetails = hasCauses || hasSymp || hasAct;
                          return (
                            <div key={ci} className={ci % 2 ? 'bg-gray-50/50' : 'bg-white'} data-testid={`dtc-row-${c.code}`}>
                              <div className="flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
                                   onClick={() => setExpandedCode(isExp ? null : c.code)}>
                                <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${c.ai_severity === 'CRITICAL' ? 'bg-red-100' : 'bg-sky-100'}`}>
                                  <CircleDot className={`h-3.5 w-3.5 ${c.ai_severity === 'CRITICAL' ? 'text-red-600' : 'text-sky-600'}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-mono font-bold text-slate-800 text-sm">{c.code}</span>
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                      c.ai_severity === 'CRITICAL' ? 'bg-red-100 text-red-700' : 'bg-sky-100 text-sky-700'
                                    }`}>{c.ai_severity}</span>
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                      c.status_label === 'Active' ? 'bg-red-50 text-red-600'
                                        : c.status_label === 'Pending' ? 'bg-orange-50 text-orange-600'
                                        : 'bg-blue-50 text-blue-600'
                                    }`}>{c.status_label}</span>
                                    {hasDetails && (
                                      <Eye className={`h-3 w-3 ml-1 ${isExp ? 'text-blue-500' : 'text-gray-300'}`} />
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                                    {c.ai_explanation || c.description || 'No description'}
                                  </p>
                                </div>
                              </div>
                              {isExp && hasDetails && (
                                <div className="px-3 pb-3 pt-0 ml-10 space-y-2">
                                  {hasCauses && <DetailBlock title="Possible Causes" items={c.possible_causes} color="amber" icon={AlertTriangle} />}
                                  {hasSymp   && <DetailBlock title="Symptoms" items={c.symptoms} color="blue" icon={Eye} />}
                                  {hasAct    && <DetailBlock title="Recommended Actions" items={c.recommended_actions} color="emerald" icon={Wrench} />}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <CheckCircle className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 font-medium">No fault codes detected</p>
              <p className="text-xs text-gray-400 mt-1">The vehicle's OBD is clean — nothing to analyse.</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ── Live Sensor Data ──────────────────────────────────────── */}
      {liveData.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold flex items-center gap-1.5 text-sm">
                <Activity className="h-4 w-4 text-blue-600" /> Live Sensor Data
              </div>
              <Badge variant="outline" className="text-[10px]">{liveData.length} reading{liveData.length !== 1 ? 's' : ''}</Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="obd-live-data-grid">
              {liveData.map((r, idx) => {
                const IconComp = getMfcwlLiveIcon(r.pid);
                return (
                  <div key={idx} className="bg-white rounded-lg border border-slate-200 p-3 hover:border-blue-300 hover:shadow-sm transition-all"
                       data-testid={`live-data-reading-${r.pid || idx}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="h-7 w-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <IconComp className="h-3.5 w-3.5 text-blue-600" />
                      </div>
                      <span className="text-xs text-slate-500 font-medium truncate">{r.name || r.pid}</span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-bold text-slate-900">
                        {typeof r.value === 'number'
                          ? r.value.toFixed(r.value % 1 === 0 ? 0 : 1)
                          : (r.value ?? '—')}
                      </span>
                      {r.unit && <span className="text-xs text-slate-400 font-medium">{r.unit}</span>}
                    </div>
                    {r.pid && <span className="text-[10px] text-slate-400 font-mono mt-1 block">PID: {r.pid}</span>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Scan History (accordion) ───────────────────────────────── */}
      {inspectionId && (
        <ScanHistory inspectionId={inspectionId}
                     apiBase="/api/b2b/mfcwl/admin/inspections" />
      )}

      {/* Scan metadata footer — always useful for debugging */}
      {obd && (
        <Card>
          <CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Scan metadata</div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="border rounded p-2 bg-gray-50">
                <div className="text-gray-500">Scan ID</div>
                <div className="font-mono truncate">{obd.scan_id}</div>
              </div>
              <div className="border rounded p-2 bg-gray-50">
                <div className="text-gray-500">Partner verdict</div>
                <div className="font-medium">{obd.summary?.ai_verdict || '—'}</div>
              </div>
              <div className="border rounded p-2 bg-gray-50">
                <div className="text-gray-500">Total DTCs</div>
                <div className="font-medium">{obd.summary?.total ?? rawCount}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Live-data PID → icon map (mirrors B2C) ─────────────────────────
const MFCWL_LIVE_ICON = { '0C': Gauge, '0D': Activity, '05': Thermometer,
                          '11': Zap, '2F': Fuel, '0F': Wind, '1F': Timer };
function getMfcwlLiveIcon(pid) { return MFCWL_LIVE_ICON[pid] || Activity; }

// ── DetailBlock for Causes/Symptoms/Actions (shared with B2C aesthetic) ─
const MFCWL_DETAIL_STYLES = {
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   icon: 'text-amber-500',   title: 'text-amber-700',   dot: 'bg-amber-400' },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    icon: 'text-blue-500',    title: 'text-blue-700',    dot: 'bg-blue-400' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-500', title: 'text-emerald-700', dot: 'bg-emerald-400' },
};

function DetailBlock({ title, items, color, icon: Icon }) {
  const s = MFCWL_DETAIL_STYLES[color] || MFCWL_DETAIL_STYLES.blue;
  return (
    <div className={`${s.bg} border ${s.border} rounded-lg p-2.5`}
         data-testid={`detail-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`h-3.5 w-3.5 ${s.icon}`} />
        <span className={`text-[11px] font-semibold ${s.title}`}>{title}</span>
      </div>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-gray-700 leading-snug">
            <span className={`${s.dot} h-1.5 w-1.5 rounded-full mt-1.5 flex-shrink-0`} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Sub-tab: Service History
// ═══════════════════════════════════════════════════════════════════
function ServiceHistoryTab({ doc, uploading, onUpload }) {
  const docs = doc?.service_history || [];
  return (
    <div className="space-y-3" data-testid="service-history-tab">
      <Card><CardContent className="p-3">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold flex items-center gap-1.5">
            <History className="h-4 w-4 text-indigo-600" /> Service History
          </div>
          <label className="cursor-pointer">
            <input type="file" accept=".pdf,image/*" className="hidden"
                   onChange={e => e.target.files?.[0] && onUpload(e.target.files[0])}
                   data-testid="upload-svc-input" />
            <Button size="sm" variant="outline" disabled={uploading === 'svc'} asChild>
              <span>{uploading === 'svc'
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Uploading…</>
                : <><Upload className="h-3.5 w-3.5 mr-1" /> Upload PDF / image</>}</span>
            </Button>
          </label>
        </div>
        {docs.length > 0 ? (
          <div className="space-y-2">
            {docs.map(sd => (
              <div key={sd.id} className="border rounded p-2 text-xs bg-gray-50">
                <div className="flex justify-between items-center">
                  <span className="font-medium truncate">{sd.original_filename}</span>
                  <Badge variant="outline" className="text-[10px]">
                    {sd.extraction_status === 'done' ? `${sd.extracted_records?.length || 0} records` : sd.extraction_status}
                  </Badge>
                </div>
                {sd.extracted_records && sd.extracted_records.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {sd.extracted_records.slice(0, 6).map((rec, i) => (
                      <li key={i} className="text-[11px] text-gray-700">
                        {rec.date} · {rec.odometer_km} km · {rec.service_type}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">
            No service history uploaded yet. Drop any job-card PDF or photo — we'll AI-extract the records.
          </p>
        )}
      </CardContent></Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Shared: Audit Trail (scoped per tab)
// ═══════════════════════════════════════════════════════════════════
const AUDIT_SCOPE_PREFIXES = {
  engine: ['engine_video', 'engine_ai', 'sound_ai'],
  obd:    ['obd'],
  service:['service_history'],
  all:    null,   // show everything
};

function TabAuditTrail({ events, loading, onRefresh, scope, testIdPrefix }) {
  const prefixes = AUDIT_SCOPE_PREFIXES[scope];
  const filtered = !prefixes
    ? events
    : events.filter(e => prefixes.some(p => (e.action || '').includes(p)));

  return (
    <Card><CardContent className="p-3">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs font-medium text-gray-700">
          {scope === 'all' ? 'Full Audit Trail' : 'Audit Trail (scoped)'}
          <span className="ml-1.5 text-[10px] text-gray-400">
            {filtered.length} event{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
        <Button size="sm" variant="ghost" className="h-6 text-[10px]"
                onClick={onRefresh} disabled={loading}
                data-testid={`${testIdPrefix}-refresh-audit`}>
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      {loading && !events.length ? (
        <div className="text-[11px] text-gray-400 italic">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-[11px] text-gray-400 italic">No audit events for this scope yet.</div>
      ) : (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {filtered.map((e, i) => <AuditRow key={e.id || i} e={e} />)}
        </div>
      )}
    </CardContent></Card>
  );
}


// ═══════════════════════════════════════════════════════════════════
// REPORTS & DELIVERY — consolidated Web / PDF / JSON + audit trail
// ═══════════════════════════════════════════════════════════════════
const OUTBOUND_STATUS_META = {
  READY:        { label: 'Ready to send',        cls: 'bg-emerald-100 text-emerald-700' },
  SENT:         { label: 'Sent (awaiting 2xx)',  cls: 'bg-blue-100 text-blue-700' },
  CONFIRMED:    { label: 'Confirmed by MFCWL',   cls: 'bg-emerald-100 text-emerald-700' },
  FAILED:       { label: 'Delivery failed',      cls: 'bg-rose-100 text-rose-700' },
  NOT_READY:    { label: 'Not ready · AI pending', cls: 'bg-amber-100 text-amber-700' },
  STALE:        { label: 'Stale · re-delivery needed', cls: 'bg-orange-100 text-orange-700' },
  VOID_RETRACT: { label: 'Voided · retract needed', cls: 'bg-rose-100 text-rose-700' },
};

function DeliveryRow({ d }) {
  const [open, setOpen] = useState(false);
  const ok = d.ok || (d.http_status && d.http_status >= 200 && d.http_status < 300);
  return (
    <div className="border rounded-md bg-white" data-testid={`outbound-delivery-${d.delivery_id}`}>
      <button type="button" onClick={() => setOpen(v => !v)}
              className="w-full text-left flex items-start gap-2.5 p-2.5">
        <span className={`shrink-0 mt-0.5 h-6 w-6 rounded-full flex items-center justify-center ${
          ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
          {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-800">
              {ok ? 'Delivered & confirmed' : 'Delivery failed'}
            </span>
            {d.http_status != null && (
              <span className={`text-[10px] px-1.5 rounded ${
                ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                HTTP {d.http_status}
              </span>
            )}
            {d.duration_ms != null && (
              <span className="text-[10px] text-slate-400">{d.duration_ms} ms</span>
            )}
          </div>
          <div className="text-[10px] text-slate-500 font-mono truncate">{d.target_url}</div>
          <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-[10px] text-slate-500">
            <span className="flex items-center gap-1">
              <ArrowRight className="h-3 w-3 text-blue-500" /> Sent: {fmtDate(d.sent_at)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-emerald-500" />
              {d.confirmed_at ? `Confirmed: ${fmtDate(d.confirmed_at)}` : 'No confirmation'}
            </span>
          </div>
          {d.error && <div className="mt-1 text-[10px] text-rose-600 truncate">Error: {d.error}</div>}
        </div>
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t px-2.5 py-2 space-y-1.5">
          <div className="text-[10px] text-slate-400">Delivery ID: <code className="text-slate-600">{d.delivery_id}</code></div>
          <div className="text-[10px] text-slate-400 break-all">Signature: <code className="text-slate-600">{d.signature}</code></div>
          {d.response_body && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">MFCWL response</div>
              <pre className="text-[9px] bg-slate-900 text-slate-100 p-2 rounded max-h-40 overflow-auto font-mono">{d.response_body}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DeliveryConfirmationPanel({ d, onResend, sending }) {
  const ok = d.ok || (d.http_status && d.http_status >= 200 && d.http_status < 300);
  return (
    <Card className={`border ${ok ? 'border-emerald-200 bg-emerald-50/40' : 'border-rose-200 bg-rose-50/40'}`}
          data-testid="outbound-confirmation-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <span className={`shrink-0 mt-0.5 h-8 w-8 rounded-full flex items-center justify-center ${
              ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
              {ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-800" data-testid="outbound-confirmation-status">
                  {ok ? 'Delivered & confirmed by MFCWL'
                      : (d.error ? 'Delivery failed' : 'Sent — no 2xx confirmation')}
                </span>
                {d.http_status != null && (
                  <span className={`text-[11px] px-1.5 py-0.5 rounded font-semibold ${
                    ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}
                    data-testid="outbound-confirmation-http">
                    HTTP {d.http_status}
                  </span>
                )}
                {d.duration_ms != null && (
                  <span className="text-[10px] text-slate-400">{d.duration_ms} ms</span>
                )}
              </div>
              <div className="text-[10px] text-slate-500 font-mono truncate mt-0.5">{d.target_url}</div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] text-slate-500">
                <span className="flex items-center gap-1">
                  <ArrowRight className="h-3 w-3 text-blue-500" /> Sent: {fmtDate(d.sent_at)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3 text-emerald-500" />
                  {d.confirmed_at ? `Confirmed: ${fmtDate(d.confirmed_at)}` : 'No confirmation'}
                </span>
              </div>
            </div>
          </div>
          <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                  onClick={() => onResend(d.target_url)} disabled={sending}
                  data-testid="outbound-resend-btn" title={`Re-send this payload to ${d.target_url}`}>
            {sending
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Re-sending…</>
              : <><RotateCcw className="h-3.5 w-3.5 mr-1" /> Re-send</>}
          </Button>
        </div>
        {d.error && (
          <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-100 rounded p-2 break-words"
               data-testid="outbound-confirmation-error">
            <b>Error:</b> {d.error}
          </div>
        )}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">MFCWL response body</div>
          {d.response_body ? (
            <pre className="text-[10px] bg-slate-900 text-slate-100 p-2.5 rounded max-h-56 overflow-auto font-mono"
                 data-testid="outbound-confirmation-response">{d.response_body}</pre>
          ) : (
            <p className="text-[11px] text-slate-400 italic" data-testid="outbound-confirmation-noresponse">
              {d.error ? 'No response — the request did not reach MFCWL.'
                       : 'MFCWL returned an empty response body.'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function OutboundApiTab({ inspectionId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [targetUrl, setTargetUrl] = useState('');
  const [showBody, setShowBody] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/inspections/${inspectionId}/outbound-api`);
      setData(r.data);
      setTargetUrl(prev => {
        if (prev) return prev;
        const real = r.data?.default_target_url;
        if (real && !String(real).includes('.example')) return real;
        return r.data?.test_target_url || '';
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load outbound API');
    } finally { setLoading(false); }
  }, [inspectionId]);

  useEffect(() => { load(); }, [load]);

  // Live SLA countdown for the API-4 send (highest fine risk — human-gated).
  const nowMs = useNow(1000);
  const api4 = deriveTimer(data?.sla?.timers?.api4, nowMs);

  const copy = (txt, label) => {
    try { navigator.clipboard.writeText(txt); toast.success(label); }
    catch { toast.error('Copy failed'); }
  };

  const send = async (overrideUrl) => {
    const url = ((typeof overrideUrl === 'string' && overrideUrl) || targetUrl).trim();
    if (!url) { toast.error('Enter a target URL first (e.g. a webhook.site URL)'); return; }
    setSending(true);
    try {
      const r = await axios.post(
        `${BASE}/inspections/${inspectionId}/outbound-api/send`,
        { target_url: url });
      if (r.data?.ok) toast.success('Delivered — confirmation received (2xx)');
      else toast.warning('Sent, but no 2xx confirmation from target');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 p-4" data-testid="outbound-api-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading outbound API…
      </div>
    );
  }
  if (!data) return null;

  const req = data.request || {};
  const meta = OUTBOUND_STATUS_META[data.status] || { label: data.status, cls: 'bg-slate-100 text-slate-600' };
  const bodyStr = req.body_str || JSON.stringify(req.body || {}, null, 2);
  const deliveries = data.deliveries || [];
  const lastDelivery = deliveries.length ? deliveries[deliveries.length - 1] : null;
  const hasRealUrl = data.default_target_url && !String(data.default_target_url).includes('.example');

  return (
    <div className="space-y-4" data-testid="outbound-api-tab">
      {/* Header + status */}
      <Card className="border border-indigo-100">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold flex items-center gap-1.5">
                <Send className="h-4 w-4 text-indigo-600" /> Outbound API → MFCWL
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Event <code className="text-slate-600">{req.body?.event || 'inspection.report.completed'}</code> —
                the completed report we push back to MFCWL (API 4).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={`text-[10px] ${meta.cls}`} data-testid="outbound-status-badge">{meta.label}</Badge>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={load}
                      data-testid="outbound-refresh-btn" title="Refresh">
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          {!data.signed && (
            <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md p-2"
                 data-testid="outbound-unsigned-warning">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              Payload is not HMAC-signed yet — set <code>MFCWL_OUTBOUND_WEBHOOK_SECRET</code> to enable request signing before hand-off.
            </div>
          )}
          {data.redelivery_needed && (
            <div className={`mt-2 flex items-start gap-1.5 text-[11px] rounded-md p-2 border ${
                   data.redelivery_kind === 'voided'
                     ? 'text-rose-700 bg-rose-50 border-rose-100'
                     : 'text-orange-700 bg-orange-50 border-orange-100'}`}
                 data-testid="outbound-redelivery-banner">
              <RotateCcw className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                <b>{data.redelivery_kind === 'voided'
                    ? 'AI analysis was voided after delivery — retract the report from MFCWL.'
                    : 'AI analysis was edited after delivery — the report sent to MFCWL is now stale.'}</b>
                {data.redelivery_reason && <> {data.redelivery_reason}.</>}
                {data.redelivery_at && <span className="text-slate-500"> ({fmtDate(data.redelivery_at)})</span>}
                {' '}Re-send below to update MFCWL.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Latest delivery confirmation + one-tap re-send */}
      {lastDelivery && (
        <DeliveryConfirmationPanel d={lastDelivery} onResend={send} sending={sending} />
      )}

      {/* Full request */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-sm flex items-center gap-1.5">
              <Code className="h-4 w-4 text-slate-700" /> Full API request
            </span>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" className="h-7 text-[11px]"
                      onClick={() => copy(req.curl || '', 'cURL command copied')}
                      data-testid="outbound-copy-curl">
                <Copy className="h-3 w-3 mr-1" /> Copy cURL
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px]"
                      onClick={() => copy(bodyStr, 'JSON body copied')}
                      data-testid="outbound-copy-json">
                <Copy className="h-3 w-3 mr-1" /> Copy JSON body
              </Button>
            </div>
          </div>

          {/* Request line */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Request</div>
            <div className="flex items-center gap-2 bg-slate-50 border rounded px-2 py-1.5 font-mono text-[11px]"
                 data-testid="outbound-request-line">
              <span className="text-emerald-700 font-semibold">{req.method}</span>
              <span className="text-slate-700 truncate">{req.url}</span>
            </div>
          </div>

          {/* Headers */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Headers</div>
            <div className="bg-slate-50 border rounded px-2 py-1.5 font-mono text-[11px] space-y-0.5 overflow-x-auto"
                 data-testid="outbound-headers">
              {Object.entries(req.headers || {}).map(([k, v]) => (
                <div key={k} className="whitespace-nowrap">
                  <span className="text-indigo-600">{k}</span>
                  <span className="text-slate-400">: </span>
                  <span className="text-slate-700">{v}</span>
                  {k === 'X-Wisedrive-Timestamp' && v && (
                    <span className="text-slate-400"> ({fmtTimeOnly(v)} IST)</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">JSON body</div>
              <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2"
                      onClick={() => setShowBody(v => !v)} data-testid="outbound-toggle-body">
                {showBody ? 'Hide' : 'Show'} body
              </Button>
            </div>
            {showBody && (
              <pre className="text-[10px] bg-slate-900 text-slate-100 p-2.5 rounded max-h-96 overflow-auto font-mono"
                   data-testid="outbound-body-json">{bodyStr}</pre>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Manual send to MFCWL */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap">
            <Send className="h-4 w-4 text-indigo-600" /> Send to MFCWL
            {api4.show && !api4.done && !api4.blocked && (
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ml-1 ${SLA_CHIP_CLS[api4.state] || ''}`}
                data-testid="outbound-sla-countdown">
                {api4.state === 'BREACHED'
                  ? <><AlertTriangle className="h-3 w-3" /> SLA overdue by {fmtCountdown(api4.remMs, true).replace(' over', '')}</>
                  : <><Clock className="h-3 w-3" /> Send within {fmtCountdown(api4.remMs, true)}</>}
              </span>
            )}
            {api4.show && api4.done && (
              <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ml-1 bg-emerald-100 text-emerald-700 border-emerald-200"
                    data-testid="outbound-sla-sent">
                <CheckCircle2 className="h-3 w-3" /> Sent within SLA
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500">
            {hasRealUrl
              ? <>Manually push the exact payload above to MFCWL's endpoint. Every send is logged
                 below with delivery &amp; confirmation status.</>
              : <>MFCWL's endpoint isn't provisioned yet — enter a URL you control
                 (e.g. a <span className="font-mono">webhook.site</span> URL) to validate the
                 signed payload. Every send is logged below.</>}
          </p>
          <div className="flex gap-2">
            <Input value={targetUrl} onChange={e => setTargetUrl(e.target.value)}
                   placeholder="https://webhook.site/your-unique-id"
                   className="h-8 text-xs font-mono" data-testid="outbound-target-url-input" />
            <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                    onClick={send} disabled={sending} data-testid="outbound-send-btn">
              {sending
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Sending…</>
                : <><Send className="h-3.5 w-3.5 mr-1" /> {hasRealUrl ? 'Send to MFCWL' : 'Send'}</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delivery activity */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="font-semibold text-sm flex items-center gap-1.5">
            <History className="h-4 w-4 text-indigo-600" /> Delivery activity
            {deliveries.length > 0 && <span className="text-[11px] text-slate-400">({deliveries.length})</span>}
          </div>
          {deliveries.length === 0 ? (
            <p className="text-xs text-slate-400 italic" data-testid="outbound-no-deliveries">
              Not yet sent to MFCWL. Each send will be logged here with sent &amp; confirmation timestamps.
            </p>
          ) : (
            <div className="space-y-2" data-testid="outbound-deliveries">
              {[...deliveries].reverse().map((d, i) => <DeliveryRow key={d.delivery_id || i} d={d} />)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ReportsDeliveryCard({ inspectionId, doc, link, linkLoading, onIssueLink }) {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);

  const analysisStatus = doc?.inspection?.analysis_status || doc?.analysis_status || 'AWAITING_VIDEO';
  const ready = analysisStatus === 'COMPLETED';
  const combined = doc;

  useEffect(() => {
    if (ready && !link && !linkLoading) { onIssueLink(); }
  }, [ready, link, linkLoading, onIssueLink]);

  const dlPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await axios.get(
        `${BASE}/inspections/${inspectionId}/report.pdf`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      const safeReg = stripSpaces(doc?.inspection?.vehicle?.reg_no) || inspectionId;
      a.href = url; a.download = `MFCWL_Inspection_${safeReg}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF generation failed');
    } finally { setPdfLoading(false); }
  };

  const dlJson = () => {
    const blob = new Blob([JSON.stringify(combined, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${inspectionId}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (txt, label = 'Copied') => {
    try { navigator.clipboard.writeText(txt); toast.success(label); } catch { /* ignore */ }
  };

  return (
    <Card className="border border-indigo-100" data-testid="reports-delivery-card">
      <CardContent className="p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-semibold flex items-center gap-1.5">
            <FileText className="h-4 w-4 text-indigo-600" /> Reports & Delivery
          </div>
          <Badge className={`text-[10px] ${ANALYSIS_STATUS_COLOR[analysisStatus] || 'bg-gray-100 text-gray-500'}`}>
            {ANALYSIS_STATUS_LABEL[analysisStatus] || analysisStatus}
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {/* WEB */}
          <div className="border rounded-md p-2 bg-white">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1">
              <Globe className="h-3.5 w-3.5 text-indigo-600" /> Web Report
            </div>
            {!ready ? (
              <div className="text-[11px] text-gray-400">Available after AI analysis completes.</div>
            ) : link ? (
              <div className="space-y-1">
                <a href={link.web_url} target="_blank" rel="noreferrer"
                   className="text-[11px] text-indigo-600 hover:underline truncate block font-mono"
                   data-testid="drawer-web-link">
                  {link.web_url.replace(/^https?:\/\//, '').slice(0, 42)}…
                </a>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                          onClick={() => window.open(link.web_url, '_blank', 'noopener')}
                          data-testid="drawer-open-web">
                    Open new tab
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2"
                          onClick={() => copyToClipboard(link.web_url, 'Web URL copied')}>
                    Copy
                  </Button>
                </div>
                <div className="text-[9px] text-gray-400">Expires in {link.ttl_days} days</div>
              </div>
            ) : (
              <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={linkLoading}
                      onClick={onIssueLink} data-testid="drawer-issue-link">
                {linkLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Generate link'}
              </Button>
            )}
          </div>

          {/* PDF */}
          <div className="border rounded-md p-2 bg-white">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1">
              <FileDown className="h-3.5 w-3.5 text-rose-600" /> MFCWL PDF
            </div>
            {!ready ? (
              <div className="text-[11px] text-gray-400">Available after AI analysis completes.</div>
            ) : (
              <div className="space-y-1">
                <div className="text-[10px] text-gray-500">MFCWL-branded inspection report</div>
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                        onClick={dlPdf} disabled={pdfLoading}
                        data-testid="drawer-download-pdf">
                  {pdfLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              : <Download className="h-3 w-3 mr-1" />} Download PDF
                </Button>
                {link?.pdf_url && (
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 ml-1"
                          onClick={() => copyToClipboard(link.pdf_url, 'PDF URL copied')}>
                    Copy tokenised URL
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* JSON */}
          <div className="border rounded-md p-2 bg-white">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1">
              <Code className="h-3.5 w-3.5 text-slate-700" /> JSON
            </div>
            <div className="space-y-1">
              <div className="text-[10px] text-gray-500">
                Combined inspection + OBD + AI payload
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                        onClick={dlJson} data-testid="drawer-download-json">
                  <Download className="h-3 w-3 mr-1" /> Download
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                        onClick={() => setRawOpen(v => !v)}
                        data-testid="drawer-toggle-json">
                  {rawOpen ? 'Hide' : 'Preview'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {rawOpen && (
          <pre className="text-[10px] bg-slate-900 text-slate-100 p-2 rounded max-h-60 overflow-auto font-mono">
{JSON.stringify(combined, null, 2).slice(0, 4000)}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}

const AUDIT_LABELS = {
  'mfcwl.partner.inspection.create':   { label: 'Inspection created by MFCWL',   icon: 'IN',  color: 'text-emerald-700' },
  'mfcwl.partner.inspection.poll':     { label: 'MFCWL polled inspection',       icon: 'PL',  color: 'text-slate-600' },
  'mfcwl.partner.engine_video.upload': { label: 'MFCWL uploaded engine video',   icon: 'EV',  color: 'text-indigo-700' },
  'mfcwl.partner.report.json.fetch':   { label: 'MFCWL pulled JSON report',      icon: 'JS',  color: 'text-blue-700' },
  'mfcwl.partner.report.pdf.fetch':    { label: 'MFCWL pulled PDF report',       icon: 'PD',  color: 'text-rose-700' },
  'mfcwl.inspection.assign':           { label: 'Mechanic assigned',             icon: 'AS',  color: 'text-indigo-700' },
  'mfcwl.inspection.unassign':         { label: 'Mechanic unassigned',           icon: 'UN',  color: 'text-rose-700' },
  'mfcwl.inspection.status':           { label: 'Status changed',                icon: 'ST',  color: 'text-amber-700' },
  'mfcwl.engine_video.upload':         { label: 'Engine video uploaded (admin)', icon: 'EV',  color: 'text-indigo-700' },
  'mfcwl.admin.engine_ai.reanalyze':   { label: 'Re-analyse triggered (admin)',  icon: 'RA',  color: 'text-violet-700' },
  'mfcwl.admin.engine_ai.override':    { label: 'Manual override applied (admin)', icon: 'OV', color: 'text-indigo-700' },
  'mfcwl.admin.engine_ai.verify':      { label: 'Verified vs AI source (admin)',   icon: 'VF', color: 'text-emerald-700' },
  'mfcwl.admin.engine_ai.sync':        { label: 'Synced from AI source (admin)',   icon: 'SY', color: 'text-blue-700' },
  'mfcwl.admin.engine_ai.restore':     { label: 'Version restored (admin)',        icon: 'RS', color: 'text-indigo-700' },
  'mfcwl.admin.obd.rescan_request':    { label: 'OBD rescan requested (admin)',  icon: 'RS',  color: 'text-amber-700' },
  'mfcwl.admin.obd.ai_analyze':        { label: 'OBD AI analysis run (admin)',   icon: 'AI',  color: 'text-purple-700' },
  'mfcwl.mechanic.obd.submit':         { label: 'Mechanic submitted OBD scan',   icon: 'OB',  color: 'text-indigo-700' },
  'mfcwl.mechanic.engine_video.upload':{ label: 'Mechanic uploaded engine video',icon: 'EV',  color: 'text-indigo-700' },
  'mfcwl.mechanic.start':              { label: 'Mechanic started inspection',   icon: 'GO',  color: 'text-indigo-700' },
  'mfcwl.mechanic.complete':           { label: 'Mechanic marked complete',      icon: 'OK',  color: 'text-emerald-700' },
};

function AuditRow({ e }) {
  const [open, setOpen] = useState(false);
  const meta = AUDIT_LABELS[e.action] || { label: e.action, icon: '·', color: 'text-gray-600' };
  const actor = e.actor_type === 'partner'
    ? (e.actor_name || e.actor_id || 'partner')
    : (e.actor_email || 'admin');
  return (
    <div className="border rounded-sm bg-slate-50 p-1.5 text-[11px]">
      <button type="button" className="w-full text-left flex items-start gap-2"
              onClick={() => setOpen(v => !v)}>
        <span className="shrink-0 text-[9px] font-mono bg-slate-200 text-slate-700 rounded px-1 py-0.5">{meta.icon}</span>
        <div className="flex-1 min-w-0">
          <div className={`font-medium ${meta.color}`}>
            {meta.label}
            {e.http_status && (
              <span className={`ml-1.5 text-[9px] px-1 rounded ${
                e.http_status >= 400 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
              }`}>
                HTTP {e.http_status}
              </span>
            )}
          </div>
          <div className="text-[10px] text-gray-500 truncate">
            {actor} · {fmtDate(e.ts)}
          </div>
        </div>
        <span className="text-gray-400 text-[10px]">{open ? '▲' : '▼'}</span>
      </button>
      {open && e.details && Object.keys(e.details).length > 0 && (
        <pre className="mt-1 text-[9px] bg-white p-1.5 rounded border font-mono overflow-auto max-h-28">
{JSON.stringify(e.details, null, 2)}
        </pre>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// TAB 2 — AI Analysis (engine video → Sound AI only)
// ═══════════════════════════════════════════════════════════════════
function AIAnalysisTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/engine-analyses`);
      setRows(r.data.analyses || []);
    } catch { toast.error('Failed to load analyses'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4 space-y-3">
      <div className="text-xs text-gray-500">
        {rows.length} engine analyses · MFCWL uploads via <code>POST /api/b2b/mfcwl/engine-analyses</code>
      </div>
      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-white" data-testid="mfcwl-analyses-table">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              {/* 6 columns of roughly equal width */}
              <col style={{ width: '16.66%' }} />
              <col style={{ width: '16.66%' }} />
              <col style={{ width: '16.66%' }} />
              <col style={{ width: '16.66%' }} />
              <col style={{ width: '16.66%' }} />
              <col style={{ width: '16.66%' }} />
            </colgroup>
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Date / Time</th>
                <th className="text-left px-3 py-2">Inspection ID</th>
                <th className="text-left px-3 py-2">Vehicle</th>
                <th className="text-left px-3 py-2">Engine video</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Report</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400">No engine analyses received from MFCWL yet</td></tr>
              ) : rows.map(a => (
                <tr key={a.id} className="border-t hover:bg-indigo-50/40" data-testid={`analysis-row-${a.id}`}>
                  <td className="px-3 py-2 text-xs text-gray-700 align-top whitespace-nowrap">
                    <div className="font-medium">{fmtDateOnly(a.created_at)}</div>
                    <div className="text-gray-500">{fmtTimeOnly(a.created_at)}</div>
                  </td>
                  <td className="px-3 py-2 align-top font-mono text-[11px] text-gray-800 break-all">{a.inspection_id}</td>
                  <td className="px-3 py-2 align-top text-xs">
                    {a.vehicle?.reg_no ? (
                      <>
                        <div className="font-mono font-semibold text-sm">{stripSpaces(a.vehicle.reg_no)}</div>
                        <div className="text-gray-500">{compactMakeModel(a.vehicle) || '—'}</div>
                      </>
                    ) : (
                      <div className="font-medium">{compactMakeModel(a.vehicle) || '—'}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Button size="sm" variant="outline" className="h-7 text-[11px]"
                            onClick={async () => {
                              try {
                                const r = await axios.get(
                                  `${BASE}/engine-analyses/${a.id}/engine-video`,
                                  { responseType: 'blob' },
                                );
                                setVideoUrl(URL.createObjectURL(r.data));
                              } catch (e) { toast.error('Video unavailable'); }
                            }}
                            data-testid={`view-video-${a.id}`}>
                      <Play className="h-3.5 w-3.5 mr-1" /> View video
                    </Button>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <AiStatusCell analysis={a} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Button size="sm" variant="outline" onClick={() => setOpenId(a.id)}
                            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800 h-7 text-[11px] gap-1"
                            data-testid={`open-analysis-${a.id}`}>
                      <Eye className="h-3.5 w-3.5" /> View & Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <EngineAnalysisModal analysisId={openId} onClose={() => setOpenId(null)} onRefresh={load} />
      <VideoPreviewModal url={videoUrl} onClose={() => setVideoUrl(null)} />
    </div>
  );
}

// ── AI Analysis status mapping + timeline popover ────────────────────
//   1. Received            — partner uploaded; we persisted the file.
//   2. Processing          — AI pipeline running.
//   3. Processed           — AI returned a result; admin hasn't pushed back.
//   4. Analysis sent       — MFCWL fetched the final report (or webhook acked).
const AI_STATUS_LABEL = {
  received:       { label: 'Received',       color: 'bg-slate-100 text-slate-700 border-slate-200' },
  processing:     { label: 'Processing',     color: 'bg-amber-100 text-amber-700 border-amber-200' },
  processed:      { label: 'Processed',      color: 'bg-blue-100 text-blue-700 border-blue-200' },
  analysis_sent:  { label: 'Analysis sent',  color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  failed:         { label: 'Failed',         color: 'bg-rose-100 text-rose-700 border-rose-200' },
};

function deriveAiStatus(a) {
  if (a.status === 'failed') return 'failed';
  if (a.analysis_sent_at || a.delivered_at) return 'analysis_sent';
  if (a.status === 'completed' || a.completed_at) return 'processed';
  if (a.result) return 'processed';
  if (a.sound_ai_analysis_id) return 'processing';
  return a.created_at ? 'received' : 'processing';
}

function AiStatusCell({ analysis: a }) {
  const code = deriveAiStatus(a);
  const cfg = AI_STATUS_LABEL[code] || AI_STATUS_LABEL.processing;

  // Timeline — each stage either has a timestamp or "pending"
  const stages = [
    { code: 'received',      ts: a.created_at },
    { code: 'processing',    ts: a.ai_submitted_at || a.created_at },
    { code: 'processed',     ts: a.completed_at || (a.result ? a.created_at : null) },
    { code: 'analysis_sent', ts: a.analysis_sent_at || a.delivered_at },
  ];
  const activeIdx = stages.findIndex(s => s.code === code);

  return (
    <div className="flex items-center gap-1.5" data-testid={`ai-status-${a.id}`}>
      <span className={`px-2 py-0.5 rounded text-[10px] border font-medium whitespace-nowrap ${cfg.color}`}>
        {cfg.label}
      </span>
      <Popover>
        <PopoverTrigger asChild>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 shrink-0"
                  title="Status history"
                  data-testid={`ai-status-log-${a.id}`}>
            <FileText className="h-3 w-3 text-gray-400 hover:text-indigo-600" />
          </Button>
        </PopoverTrigger>
        <PopoverContent side="left" className="w-72 p-0" data-testid={`ai-status-popover-${a.id}`}>
          <div className="px-3 py-2 border-b text-xs font-semibold text-slate-700">
            Status timeline
          </div>
          <ol className="p-3 space-y-2">
            {stages.map((s, i) => {
              const stageCfg = AI_STATUS_LABEL[s.code];
              const done = !!s.ts && i <= activeIdx;
              const current = i === activeIdx;
              return (
                <li key={s.code} className="flex items-start gap-2.5">
                  <div className="relative flex flex-col items-center">
                    <span className={`h-2 w-2 rounded-full mt-1.5 ${
                      done ? 'bg-emerald-500' : current ? 'bg-amber-400' : 'bg-slate-300'
                    }`} />
                    {i < stages.length - 1 && (
                      <span className={`w-px flex-1 mt-1 ${done ? 'bg-emerald-300' : 'bg-slate-200'}`}
                            style={{ minHeight: '18px' }} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className={`text-xs ${done ? 'font-medium text-slate-900' : 'text-slate-500'}`}>
                      {stageCfg.label}
                    </div>
                    <div className="text-[10px] text-slate-400">
                      {s.ts ? fmtDate(s.ts) : (current ? 'In progress…' : 'Pending')}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function VideoPreviewModal({ url, onClose }) {
  const handleClose = () => {
    if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
    onClose();
  };
  return (
    <Dialog open={!!url} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="video-preview-modal">
        <DialogHeader className="px-4 pt-3">
          <DialogTitle className="text-sm">Engine video</DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-4">
          {url && (
            <video src={url} controls autoPlay className="w-full rounded border bg-black"
                   data-testid="engine-video-player" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EngineAnalysisModal({ analysisId, onClose, onRefresh }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('engine');
  const [uploading, setUploading] = useState(null);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [link, setLink] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);

  const EA_BASE = `${BASE}/engine-analyses`;

  const load = useCallback(async () => {
    if (!analysisId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${EA_BASE}/${analysisId}/report.json`);
      setDoc(r.data);
    } catch { toast.error('Fetch failed'); }
    finally { setLoading(false); }
  }, [analysisId, EA_BASE]);

  const loadAudit = useCallback(async () => {
    if (!analysisId) return;
    setAuditLoading(true);
    try {
      const r = await axios.get(`${EA_BASE}/${analysisId}/audit`);
      setAudit(r.data);
    } catch { /* silent */ }
    finally { setAuditLoading(false); }
  }, [analysisId, EA_BASE]);

  useEffect(() => { if (analysisId) { load(); loadAudit(); } }, [analysisId, load, loadAudit]);

  const issueLink = useCallback(async () => {
    setLinkLoading(true);
    try {
      const r = await axios.post(`${EA_BASE}/${analysisId}/public-link`, { ttl_days: 7 });
      setLink(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Link generation failed'); }
    finally { setLinkLoading(false); }
  }, [analysisId, EA_BASE]);

  const uploadSoundAi = async (file) => {
    setUploading('sound');
    const fd = new FormData(); fd.append('audio_file', file);
    fd.append('inspection_id', doc?.inspection?.inspection_id || '');
    try {
      // Admin re-uploads aren't supported on engine-analyses today —
      // mechanics must re-submit via the partner endpoint. Fall back to
      // reanalyse-on-saved-file for admin convenience.
      toast.info('For AI-only analyses, please use Re-analyse to re-run on the existing audio.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); }
  };

  const reanalyze = async () => {
    setReanalyzing(true);
    try {
      await axios.post(`${EA_BASE}/${analysisId}/reanalyze`);
      toast.success('Re-analysis queued');
      load(); loadAudit();
    } catch (e) { toast.error(e.response?.data?.detail || 'Re-analyse failed'); }
    finally { setReanalyzing(false); }
  };

  const insp = doc?.inspection;
  const events = audit?.events || [];

  return (
    <Dialog open={!!analysisId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-[98vw] w-[98vw] h-[98vh] max-h-[98vh] p-0 rounded-xl overflow-hidden flex flex-col bg-slate-50 gap-0"
        data-testid="mfcwl-analysis-modal">
        {loading || !doc ? (
          <div className="flex-1 grid place-items-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-6 px-6 py-4 bg-white border-b border-slate-200 shrink-0">
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-xl font-semibold text-slate-900 tracking-tight truncate">
                  {insp?.inspection_id} <span className="text-slate-400 font-normal">· AI only</span>
                </DialogTitle>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Badge className={STATUS_COLOR[insp?.status] || 'bg-gray-100'}>{insp?.status}</Badge>
                  <Badge variant="outline">{insp?.service_tier}</Badge>
                  <Badge variant="outline">{activeCurrencySymbol()}{insp?.unit_price_inr}</Badge>
                  {analysisId && (
                    <Badge variant="secondary" className="font-mono text-[10px]">{analysisId.slice(0, 12)}</Badge>
                  )}
                </div>
              </div>
              <div className="hidden md:block text-xs max-w-lg text-right">
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-0.5">Vehicle</div>
                {insp?.vehicle?.reg_no
                  ? <div className="font-mono font-semibold text-sm text-slate-900">{stripSpaces(insp.vehicle.reg_no)}</div>
                  : null}
                <div className="text-slate-500">{compactMakeModel(insp?.vehicle) || '—'}</div>
                <div className="text-slate-400 text-[10px]">
                  {[insp?.vehicle?.transmission, insp?.vehicle?.fuel_type,
                    insp?.vehicle?.km_driven != null && `${(insp.vehicle.km_driven / 1000).toFixed(0)}k km`,
                    insp?.vehicle?.has_turbo ? 'Turbo' : 'No turbo']
                    .filter(Boolean).join(' · ')}
                </div>
              </div>
            </div>

            <div className="flex flex-1 min-h-0">
              <Tabs value={activeTab} onValueChange={setActiveTab}
                    className="flex flex-1 min-h-0" orientation="vertical">
                <div className="w-56 shrink-0 border-r border-slate-200 bg-white overflow-y-auto p-3">
                  <TabsList className="flex flex-col items-stretch h-auto bg-transparent p-0 gap-1 w-full"
                            data-testid="mfcwl-analysis-modal-tabs">
                    <TabsTrigger value="engine"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="ea-tab-engine-ai">
                      <Volume2 className="h-4 w-4" /> Engine AI
                    </TabsTrigger>
                    <TabsTrigger value="service"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="ea-tab-service">
                      <History className="h-4 w-4" /> Service History
                    </TabsTrigger>
                    <TabsTrigger value="reports"
                      className="justify-start gap-2 px-3 py-2 h-auto text-sm text-slate-600 rounded-md data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-none border border-transparent data-[state=active]:border-slate-200"
                      data-testid="ea-tab-reports">
                      <FileText className="h-4 w-4" /> Reports
                    </TabsTrigger>
                  </TabsList>
                </div>

                <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-slate-50">
                  <div className="max-w-7xl mx-auto w-full space-y-6 text-sm">
                    <TabsContent value="engine" className="mt-0 space-y-4 focus-visible:ring-0">
                      <EngineAiTab
                        inspectionId={analysisId}
                        doc={doc}
                        uploading={uploading}
                        onUpload={uploadSoundAi}
                        onReanalyze={reanalyze}
                        reanalyzing={reanalyzing}
                        onRefresh={() => { load(); loadAudit(); }}
                      />
                    </TabsContent>

                    <TabsContent value="service" className="mt-0 space-y-4 focus-visible:ring-0">
                      <LiveProgressServiceHistoryTab
                        inspection={{
                          id: analysisId,
                          kms_driven: insp?.vehicle?.km_driven,
                          vehicle_make: insp?.vehicle?.make,
                          vehicle_model: insp?.vehicle?.model,
                          car_number: insp?.vehicle?.reg_no,
                        }}
                        currentOdometer={insp?.vehicle?.km_driven}
                        apiBase="/api/b2b/mfcwl/admin/engine-analyses"
                        tabValue="service"
                      />
                      <TabAuditTrail events={events} loading={auditLoading} onRefresh={loadAudit}
                                     scope="service" testIdPrefix="ea-service" />
                    </TabsContent>

                    <TabsContent value="reports" className="mt-0 space-y-4 focus-visible:ring-0">
                      <ReportsDeliveryCard
                        inspectionId={analysisId}
                        doc={doc}
                        link={link}
                        linkLoading={linkLoading}
                        onIssueLink={issueLink}
                      />
                      <TabAuditTrail events={events} loading={auditLoading} onRefresh={loadAudit}
                                     scope="all" testIdPrefix="ea-reports" />
                    </TabsContent>
                  </div>
                </div>
              </Tabs>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB 3 — Invoices (monthly rollup)
// ═══════════════════════════════════════════════════════════════════
function InvoicesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/invoices`);
      setRows(r.data.invoices || []);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4 space-y-3">
      <div className="flex justify-between text-xs text-gray-500">
        <div>{rows.length} monthly invoice{rows.length !== 1 ? 's' : ''}</div>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
      </div>
      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid="mfcwl-invoices-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Month</th>
                <th className="text-right px-3 py-2">Inspections</th>
                <th className="text-left px-3 py-2">Tiers used</th>
                <th className="text-right px-3 py-2">Total (INR)</th>
                <th className="text-left px-3 py-2">Latest</th>
                <th className="text-right px-3 py-2">PDF</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400">No billable inspections yet</td></tr>
              ) : rows.map(r => (
                <tr key={r.month} className="border-t">
                  <td className="px-3 py-2 font-mono">{r.month}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                  <td className="px-3 py-2 text-xs">{(r.tiers || []).join(', ')}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{activeCurrencySymbol()} {Number(r.total_inr).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(r.latest)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" disabled title="PDF generation wired next iteration">
                      <FileText className="h-3.5 w-3.5 mr-1" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-gray-400 italic">
        PDF invoice auto-generation is scheduled for month-end. For now, row totals reflect every completed inspection's tier price.
      </p>
    </div>
  );
}
import { activeCurrencySymbol } from '@/utils/currency';
